import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import InputMask from 'react-input-mask';
import { toast } from 'react-toastify';
import { FaCheckCircle, FaCheckSquare, FaExclamationTriangle, FaPaperclip, FaPlus, FaRegSquare, FaTrash } from 'react-icons/fa';
import { FinField, FinInput, FinTextarea, FinCheck, FinChecks } from '../../components/FinField';
import ReactSelect from '../../components/ReactSelect';
import {
    TIPOS_CONTA, TIPOS_PIX, PAPEIS_CONTATO, PRAZOS_XML, PORTES_EMPRESA, opcoesDe, placeholderDe,
    TIPOS_FORNECEDOR, DOCUMENTOS_FISCAIS, REGIMES_IRPJ, REGIMES_IBS_CBS,
    DESTAQUE_DOCUMENTOS, TRIBUTOS_RETENCAO, TIPOS_DOCUMENTO,
    retencoesVazias, formatarTamanho, rotuloDe, cnpjValido, cpfValido,
    NATUREZAS_CADASTRO, REGIMES_ESPECIFICOS, SIM_NAO, semestresVigencia, CONTRIBUINTE_ICMS, CAMINHOS_SUBMISSAO,
} from '../../util/fornecedorCadastro';
// As classes `upload-*` (container, faixa, logo) moram no CSS do UploadNotaFiscal —
// é o mesmo cabeçalho, então reusar o arquivo mantém as duas telas públicas em
// sincronia em vez de duplicar a regra e deixar uma envelhecer sozinha.
import './UploadNotaFiscal.css';
import '../../css/financeiro-parcela-pagar.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost';

const contatoVazio = (cargo = '') => ({ cargo, nome: '', funcao: '', email: '', celular: '' });

// O CNPJ sem máscara e em caixa alta, como o servidor compara (o alfanumérico tem letra).
const limparCnpj = (cnpj) => String(cnpj || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();

// CNPJ com máscara a partir de qualquer forma — só dígitos, já formatado ou alfanumérico: tira o
// que não é caractere e remonta, então formatar duas vezes dá o mesmo. Fora de 14 volta como veio.
const formatarCnpj = (cnpj) => {
    const limpo = limparCnpj(cnpj);
    return limpo.length === 14
        ? limpo.replace(/^(.{2})(.{3})(.{3})(.{4})(.{2})$/, '$1.$2.$3/$4-$5')
        : (cnpj || '');
};

// Selo do caminho no "Preenchendo para": a cor diz o que a aprovação faz com o cadastro.
const PILL_CAMINHO = { atualizacao: 'is-ok', novo: 'is-info', troca_cnpj: 'is-warn' };

/**
 * O formulário em branco. O país padrão é resolvido NA LISTA que o servidor mandou.
 *
 * FORMULÁRIO EM BRANCO. O backend não devolve o cadastro, e a tela não pré-preenche nada com ele.
 *
 * O link é um token que chega por e-mail e não autentica quem abre — um encaminhamento entregaria
 * nome, telefone e e-mail dos contatos, que são dado pessoal de pessoas físicas, além de endereço,
 * inscrições e conta bancária. Preencher do zero custa alguns minutos ao fornecedor; expor custa a
 * base legal do tratamento.
 *
 * Vale também para a correção de uma rejeição e para a troca de CNPJ: nem o que o fornecedor enviou
 * volta pelo link, que pode ter sido encaminhado, nem o que foi digitado para um CNPJ passa para o
 * outro. O único dado que entra é o CNPJ do acesso, que a própria pessoa digitou.
 */
const cadastroEmBranco = (paises) => ({
    // Lista de rótulos marcados como "não se aplica" — ver alternarNaoAplica.
    nao_aplica: [],
    razaosocial: '',
    nomefantasia: '',
    cnpj: '',
    inscricaoestadual: '',
    inscricaomunicipal: '',
    indicadorinscricao: '',
    cnae_principal: '',
    cnaes_secundarios: '',
    porte: '',
    data_constituicao: '',
    endereco: {
        cep: '',
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        uf: '',
        // ID, não nome: `participante.enderecopais` é FK para `pais`.
        // O padrão é resolvido NA LISTA que o servidor mandou — cravar o
        // id do Brasil aqui seria número mágico que quebra em silêncio se
        // a tabela de referência mudar.
        pais: (paises || []).find(p => p.nome === 'Brasil')?.id || '',
        municipio: null,
    },
    // Semeia os quatro papéis do anexo para o fornecedor ver o que se
    // espera dele. Os que ficarem em branco são descartados no envio.
    contatos: PAPEIS_CONTATO.map(p => contatoVazio(p.valor)),
    email_xml: '',
    prazo_envio_xml: '',
    banco: {
        nome_banco: '',
        agencia: '',
        conta: '',
        // Vazio por padrão: 'corrente' vinha sempre e fazia a tela de
        // revisão marcar "alterado" numa seção que ninguém tocou.
        tipo_conta: '',
        pix_tipo: '',
        pix_chave: '',
        titular: '',
        cnpj_favorecido: '',
    },

    // Seções 4 a 7.
    fiscal: {
        tipo_fornecedor: '',
        documentos_fiscais: [],
        documentos_fiscais_outro: '',
        itens_fornecidos: '',
        nbs: '',
        locais_execucao: '',
        regime_irpj: '',
        regime_irpj_fundamento: '',
        regime_ibs_cbs: '',
        vigencia_opcao: '',
        data_opcao_simples: '',
        opcao_proxima_limite: '',
        regime_especifico: '',
        regime_especifico_outro: '',
        reducao_percentual: '',
        contribuinte_iss: '',
        destaque_documentos: '',
        credito_presumido_aplica: '',
        diferimento_aplica: '',
        cst: '',
        cclasstrib: '',
        diferimento_suspensao: '',
        retencoes: retencoesVazias(),
        observacao_fiscal: '',
        ncm: '',
        retencoes_outras: '',
        sem_retencoes: false,
        data_transicao_iva: '',
    },

    // Cabeçalho do anexo.
    natureza: '',

    // Seção 10 — quem assina.
    assinante_nome: '',
    assinante_cpf: '',
    assinante_cargo: '',
    assinatura_local: '',
    assinatura_data: '',

    // Seção 10 — o aceite NUNCA volta preenchido, nem na rejeição. Ele é
    // do envio, não do rascunho: reaproveitá-lo registraria concordância
    // com um texto que, nesta rodada, ele não leu.
    aceite: [],
});

/**
 * Formulário de cadastro preenchido pelo próprio fornecedor, por link tokenizado.
 *
 * Mesmo molde do UploadNotaFiscal: rota pública sem `PrivateRoute`, axios cru sem
 * token de sessão, e o token da URL é a única credencial. Fica em
 * `FornecedorPublico/` de propósito — separado do fluxo do convidado, que é outra
 * coisa e não deve se misturar com este.
 *
 * O que o fornecedor envia NÃO vira cadastro direto: vira submissão em revisão.
 * Dado que chega de fora sem autenticação não sobrescreve cadastro sem alguém olhar.
 */
/**
 * Moldura da página. Mora AQUI, no escopo do módulo, e não dentro do componente.
 *
 * Declarada lá dentro, ela seria uma função nova a cada render — o React compara
 * `element.type` por identidade, então trocaria o tipo e desmontaria a árvore
 * inteira em vez de reconciliar. Na prática: o input perdia o foco a cada tecla e
 * o formulário ficava impossível de preencher.
 */
/**
 * Pergunta com o marcador "não se aplica".
 *
 * Mora AQUI, e não no `FinField`, porque o N/A é desta tela: o `FinField` é usado por
 * cinquenta telas e nenhuma outra pede isso. Ele é usado como está, sem alteração —
 * o rótulo dele já aceita um node, então o badge entra junto do texto.
 *
 * `naKey` é o CAMINHO DO CAMPO no payload (`razaosocial`, `endereco.cep`,
 * `fiscal.regime_irpj`) e é obrigatório. Não cai para o rótulo de propósito: o
 * `nao_aplica` que o servidor grava é lido pelo `regraNaoAplica()`, que decide
 * `required` vs `nullable` comparando string. Com o rótulo como chave, renomear
 * "Razão social" para "Razão Social" no JSX fazia o `in_array` estrito falhar — o
 * fornecedor via o badge ligado, o campo travado e vazio, e um erro exigindo
 * preencher que ele não tinha como resolver. Sem erro em log nenhum.
 *
 * Caminho do campo é o mesmo identificador que a regra de validação já usa, então o
 * servidor compara contra a chave e não contra o texto exibido — renomear o rótulo
 * deixa de ter efeito na validação.
 *
 * Marcar DESABILITA, não apaga: o valor fica na tela e volta ao desmarcar. A prop é a
 * que o controle entende — `isDisabled` no ReactSelect, `disabled` no resto —, que é
 * como o Cadastro de Evento já desabilita campo por campo.
 */
/**
 * Campos que a área definiu como obrigatórios — sem opção "não se aplica".
 *
 * Caminho do campo no payload, o mesmo identificador do `naKey`. Esta lista é a
 * fonte única: o JSX marca `obrigatorio` para tirar o badge e pôr o asterisco, e a
 * trava do botão Enviar percorre ESTA lista para decidir se pode enviar. Fossem
 * duas listas, uma acabaria com um campo que a outra não tem, e o botão travaria
 * sem dizer qual campo falta — ou liberaria um envio que o servidor recusa.
 *
 * Fora daqui, porque dependem de outra resposta: inscrição estadual e municipal
 * (pelo tipo de fornecedor), NCM/NBS (um dos dois basta), documentos anexos e os
 * contatos. Cada uma tem sua própria checagem em `pendencias()`.
 */
const OBRIGATORIOS = [
    'natureza', 'razaosocial', 'cnpj',
    'endereco.cep', 'endereco.logradouro', 'endereco.numero',
    'endereco.bairro', 'endereco.municipio', 'endereco.uf',
    'email_xml', 'prazo_envio_xml',
    'banco.nome_banco', 'banco.agencia', 'banco.conta', 'banco.tipo_conta',
    'banco.titular', 'banco.cnpj_favorecido',
    'fiscal.tipo_fornecedor', 'fiscal.itens_fornecidos', 'fiscal.locais_execucao',
    'fiscal.regime_irpj', 'fiscal.vigencia_opcao', 'fiscal.regime_ibs_cbs', 'fiscal.regime_especifico',
    'fiscal.destaque_documentos', 'fiscal.cst', 'fiscal.cclasstrib',
    'fiscal.credito_presumido_aplica', 'fiscal.diferimento_aplica',
    'assinante_nome', 'assinante_cpf', 'assinatura_data',
];

/** Anexos exigidos de todo fornecedor. */
const DOCUMENTOS_OBRIGATORIOS = ['cartao_cnpj', 'comprovante_bancario'];


/**
 * Anexos: os mesmos números do back. Eles vão no POST do cadastro, e o Cloud Run recusa
 * requisição acima de 32 MiB — a partir de 30 MB no conjunto, o cadastro sai sem eles e
 * os arquivos sobem um a um logo depois (ver `enviar`).
 */
const EXTENSOES_ANEXO = ['pdf', 'png', 'jpg', 'jpeg'];
const TAMANHO_MAXIMO_ANEXO = 10 * 1024 * 1024;
const TAMANHO_TOTAL_ANEXOS = 30 * 1024 * 1024;
const MAX_ANEXOS = 15;

/**
 * Tipos de fornecedor que fornecem MERCADORIA (exige inscrição estadual) e que
 * prestam SERVIÇO (exige inscrição municipal).
 *
 * `transportadora` fica fora das duas: transporte intermunicipal é ICMS e o
 * municipal é ISS, então o tipo sozinho não diz qual inscrição ele tem. Exigir a
 * errada trava um cadastro válido, e é justamente o que a área pediu para não
 * fazer com as datas de vigência.
 */
const TIPOS_COM_MERCADORIA = ['mercadorias', 'mercadorias_servicos'];
const TIPOS_COM_SERVICO = ['servicos', 'mercadorias_servicos'];

const SEMESTRES_VIGENCIA = semestresVigencia();

// As duas formas de recolhimento do IBS/CBS que a área pediu para o optante do Simples.
const FORMAS_SIMPLES = ['simples_nacional', 'simples_regime_regular'];

const NaoAplicaContext = React.createContext({ marcados: [], alternar: () => {} });

function CampoNA({ label, naKey, obrigatorio = false, semNA = false, children, ...resto }) {
    const { marcados, alternar } = React.useContext(NaoAplicaContext);
    const chave = naKey;
    // Obrigatório e "não se aplica" são a mesma pergunta com respostas opostas: se a
    // área decidiu que o campo tem de vir preenchido, não existe resposta "não tenho
    // este dado". O badge some, o asterisco aparece, e o campo nunca fica
    // desabilitado — nem que a chave esteja no `nao_aplica` de uma submissão antiga,
    // de quando ele ainda era opcional.
    // `semNA` tira só o badge, sem o asterisco: campo que não tem como "não se aplicar"
    // (o "Outro documento", opcional por natureza) ou que já respondeu (anexo escolhido).
    const marcado = !obrigatorio && !semNA && marcados.includes(chave);

    const badge = (
        <span
            role="button"
            tabIndex={0}
            aria-pressed={marcado}
            className={"fin-pp__minitag" + (marcado ? " is-info" : "")}
            onClick={() => alternar(chave)}
            onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); alternar(chave); } }}
            title={marcado ? 'Voltar a responder' : 'Marcar como não aplicável'}
            // `borderRadius` inline, não no `.fin-pp__minitag`: a classe é usada em
            // dezenas de telas com o cantinho de 6px, e o formato de cápsula é deste
            // badge — ele fica colado no rótulo e precisa ler como marcador, não como
            // mais uma caixinha na linha.
            style={{ marginLeft: 8, borderRadius: 999, cursor: 'pointer', textTransform: 'none', letterSpacing: 0, userSelect: 'none',
                display: 'inline-flex', alignItems: 'center', gap: 4, lineHeight: 1, padding: '4.5px 8px' }}
        >
            {/* A caixa é SVG do react-icons, como o local herdado do aéreo (FormTrecho): herda a cor
                do selo, então acompanha o `is-info` quando marcado, e não depende da fonte ter o glifo.
                `lineHeight: 1` tira a sobra da linha do rótulo, que deixava a caixa quase 1px abaixo do
                centro das letras; o padding devolve a altura que o selo tinha. */}
            {marcado ? <FaCheckSquare size={10} style={{ flexShrink: 0 }} /> : <FaRegSquare size={10} style={{ flexShrink: 0 }} />}
            N/A
        </span>
    );

    // Marcado, o campo diz o que ele é: "Não se aplica" no lugar do placeholder
    // original. Desabilitado e vazio, sem isso, é indistinguível de campo que a tela
    // travou por outro motivo — e é justamente onde o fornecedor para para entender.
    //
    // O `style` é MESCLADO, não trocado: alguns controles chegam aqui com estilo
    // próprio, e substituir o objeto inteiro apagaria o alinhamento deles.
    const controle = React.Children.map(children, (filho) => (
        React.isValidElement(filho)
            ? React.cloneElement(filho, Object.assign(
                filho.type === ReactSelect
                    ? { isDisabled: marcado || filho.props.isDisabled }
                    : { disabled: marcado || filho.props.disabled },
                marcado ? {
                    placeholder: 'Não se aplica',
                    style: { ...(filho.props.style || {}), fontStyle: 'italic' },
                } : {}
            ))
            : filho
    ));

    // `data-campo` é por onde o Enviar acha o campo pendente para focar (ver `tentarEnviar`).
    // `display: contents` não gera caixa: o FinField continua sendo o item da linha.
    return (
        <div data-campo={naKey} data-rotulo={typeof label === 'string' ? label : undefined} style={{ display: 'contents' }}>
            <FinField {...resto} required={obrigatorio || resto.required} label={<>{label}{obrigatorio || semNA ? null : badge}</>}>
                <div style={marcado ? { opacity: 0.5 } : null}>{controle}</div>
            </FinField>
        </div>
    );
}

const Moldura = ({ children }) => (
    <div className="upload-nf-container fin-pp">
        {/* Mesma faixa de marca do UploadNotaFiscal — o molde declarado desta tela.
            Um formulário que pede CNPJ e dados bancários chegando sem identidade, ao
            lado de um de nota fiscal que chega com ela, é o que faz o fornecedor
            desconfiar de golpe. O `onError` cai para o texto quando a imagem não
            carrega, em vez de deixar o cabeçalho vazio. */}
        <header className="upload-header" style={{
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            maxHeight: '150px',
            padding: 0
        }}>
            <div className="header-content" style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '25px 20px',
                maxHeight: '150px'
            }}>
                <img
                    src="/images/access-financeiro-logo.png"
                    alt="Access"
                    className="header-logo"
                    style={{ maxWidth: '120px', height: 'auto', marginBottom: '10px' }}
                    onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                    }}
                />
                <div className="header-logo-text" style={{ display: 'none' }}>
                    ACCESS MKT
                </div>
                <p style={{ fontSize: '13px', color: '#ffffff', margin: 0, textAlign: 'center' }}>
                    Atualização cadastral de fornecedores
                </p>
            </div>
        </header>

        <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 16px 40px', width: '100%' }}>
            {children}
        </div>
    </div>
);

export default function CadastroFornecedorPublico() {
    const { token } = useParams();

    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(null);
    const [contexto, setContexto] = useState(null);
    const [enviando, setEnviando] = useState(false);
    const [enviado, setEnviado] = useState(false);
    const [erroEnvio, setErroEnvio] = useState(null);

    const [dados, setDados] = useState(null);

    // Seção 9 — os arquivos escolhidos, ainda só neste navegador: { chave, tipo, arquivo }.
    // Vivem fora de `dados` porque não vão no JSON do cadastro, e sim ao lado dele no envio.
    const [documentos, setDocumentos] = useState([]);
    const [erroAnexo, setErroAnexo] = useState(null);
    // Caminho reserva, com 30 MB ou mais de anexos: o cadastro já foi e os arquivos sobem um a
    // um pelo link da resposta. { link, fila: [{ ...documento, ok }], atual, total, subindo, erro }.
    const [anexosDepois, setAnexosDepois] = useState(null);

    // Passo do CNPJ: o que está digitado, e a resposta da consulta quando o CNPJ não tem cadastro
    // (aí a tela pergunta se já era fornecedor). `acesso` é o CNPJ confirmado — nulo até ele, e o
    // formulário só aparece com ele: { cnpj, caminho, cnpjAntigo, jaSubmetido, statusRevisao,
    // motivoRejeicao }.
    const [cnpjDigitado, setCnpjDigitado] = useState('');
    const [consulta, setConsulta] = useState(null);
    const [jaEra, setJaEra] = useState('');
    const [cnpjAntigo, setCnpjAntigo] = useState('');
    const [consultando, setConsultando] = useState(false);
    const [erroCnpj, setErroCnpj] = useState(null);
    const [avisoAntigo, setAvisoAntigo] = useState(null);
    const [acesso, setAcesso] = useState(null);
    // "Possui redução, alíquota zero, isenção ou imunidade": só abre o campo do qual e fundamento.
    // A resposta é o próprio texto (`reducao_percentual`), então isto não viaja no envio.
    const [possuiReducao, setPossuiReducao] = useState(false);

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            const { data } = await axios.get(`${API}/public/cadastro-fornecedor/${token}`);

            setContexto(data.dados);
            setDados(cadastroEmBranco(data.dados.paises));
            setDocumentos([]);
            setErroAnexo(null);
            setPossuiReducao(false);
        } catch (e) {
            setErro(e.response?.data?.mensagem || 'Não foi possível carregar o formulário.');
        } finally {
            setCarregando(false);
        }
    }, [token]);

    useEffect(() => { carregar(); }, [carregar]);

    const set = (campo, valor) => setDados(d => ({ ...d, [campo]: valor }));
    const setEndereco = (campo, valor) => setDados(d => ({ ...d, endereco: { ...d.endereco, [campo]: valor } }));
    const setBanco = (campo, valor) => setDados(d => ({ ...d, banco: { ...d.banco, [campo]: valor } }));

    const setFiscal = (campo, valor) => setDados(d => ({ ...d, fiscal: { ...d.fiscal, [campo]: valor } }));

    const setRetencao = (tributo, campo, valor) => setDados(d => ({
        ...d,
        fiscal: {
            ...d.fiscal,
            retencoes: {
                ...d.fiscal.retencoes,
                [tributo]: { ...(d.fiscal.retencoes?.[tributo] || {}), [campo]: valor },
            },
        },
    }));

    /**
     * "Nenhuma retenção incide" — desmarca os oito tributos junto.
     *
     * As duas afirmações são opostas, e o back recusa a combinação. Aqui a tela
     * resolve antes: marcar limpa o `aplica` dos oito. A alíquota e a observação
     * ficam onde estão, só desabilitadas — desmarcar por engano não pode apagar o
     * que a pessoa escreveu, e remarcar devolve tudo.
     */
    const marcarSemRetencoes = (marcado) => setDados(d => ({
        ...d,
        fiscal: {
            ...d.fiscal,
            sem_retencoes: marcado,
            retencoes: !marcado ? d.fiscal.retencoes : Object.keys(d.fiscal.retencoes || {}).reduce((acc, t) => {
                acc[t] = { ...(d.fiscal.retencoes[t] || {}), aplica: false };
                return acc;
            }, {}),
        },
    }));

    /** Marca/desmarca um item de lista (documentos fiscais, declarações do aceite). */
    const alternarNaLista = (lista, valor) => (lista || []).includes(valor)
        ? (lista || []).filter(v => v !== valor)
        : [...(lista || []), valor];

    // Seção 9 — o arquivo fica neste navegador até o "Enviar" e sobe junto com o cadastro.
    // Subir na hora da escolha deixava o arquivo no servidor à espera, visível a quem abrisse
    // o mesmo link. Aqui só se confere o que o back também confere, para avisar antes.
    const anexar = (tipo, arquivos) => {
        if (!arquivos || arquivos.length === 0) return;

        const recusados = [];
        const aceitos = [];
        arquivos.forEach(arquivo => {
            const extensao = (arquivo.name.split('.').pop() || '').toLowerCase();
            if (!EXTENSOES_ANEXO.includes(extensao)) {
                recusados.push(`${arquivo.name} não é PDF, PNG ou JPG`);
            } else if (arquivo.size > TAMANHO_MAXIMO_ANEXO) {
                recusados.push(`${arquivo.name} passa de 10 MB`);
            } else {
                aceitos.push(arquivo);
            }
        });

        const cabem = Math.max(0, MAX_ANEXOS - documentos.length);
        if (aceitos.length > cabem) {
            recusados.push(`o envio aceita até ${MAX_ANEXOS} arquivos`);
        }

        setDocumentos(lista => [
            ...lista,
            ...aceitos.slice(0, cabem).map((arquivo, indice) => ({
                chave: `${tipo}-${Date.now()}-${indice}-${arquivo.name}`,
                tipo,
                arquivo,
            })),
        ]);
        setErroAnexo(recusados.length > 0 ? `Não anexado: ${recusados.join('; ')}.` : null);
    };

    const removerAnexo = (chave) => {
        setDocumentos(lista => lista.filter(doc => doc.chave !== chave));
        setErroAnexo(null);
    };

    const tamanhoAnexos = documentos.reduce((soma, doc) => soma + (doc.arquivo?.size || 0), 0);

    const setContato = (indice, campo, valor) => setDados(d => {
        const contatos = d.contatos.map((c, i) => i === indice ? { ...c, [campo]: valor } : c);
        return { ...d, contatos };
    });

    /**
     * "Não se aplica" por pergunta.
     *
     * Guardado como lista de CAMINHOS DE CAMPO (`razaosocial`, `endereco.cep`) —
     * ver o `naKey` do `CampoNA`, que é quem define a chave.
     *
     * Marcar NÃO apaga o valor do estado: desmarcar por engano não pode destruir o
     * que a pessoa digitou. Quem tira o valor é o envio, no
     * `semCamposNaoAplicaveis` — o campo volta intacto se ela desmarcar antes de
     * enviar, e o que chega ao servidor nunca é "não se aplica" com valor junto.
     */
    const alternarNaoAplica = (caminho) => setDados(d => ({
        ...d,
        nao_aplica: (d.nao_aplica || []).includes(caminho)
            ? (d.nao_aplica || []).filter(c => c !== caminho)
            : [...(d.nao_aplica || []), caminho],
    }));

    /**
     * Tira do corpo do POST o valor de toda pergunta marcada como "não se aplica".
     *
     * Sem isto, marcar "CNPJ não se aplica" depois de digitar o CNPJ mandava as duas
     * coisas: o servidor relaxa a regra para `nullable` mas aplica o valor mesmo
     * assim, e o cadastro terminava com CNPJ numa pergunta declarada inaplicável.
     *
     * Funciona porque a chave do N/A É o caminho do campo. Caminho que não existe no
     * corpo — os `documentos.*`, que são upload e não campo — não acha nada e não
     * faz nada.
     */
    const semCamposNaoAplicaveis = (corpo, marcados) => {
        const limpo = JSON.parse(JSON.stringify(corpo));

        (marcados || []).forEach((caminho) => {
            const partes = String(caminho).split('.');
            let no = limpo;

            for (let i = 0; i < partes.length - 1; i++) {
                no = no ? no[partes[i]] : null;
            }

            if (no && typeof no === 'object') {
                delete no[partes[partes.length - 1]];
            }
        });

        return limpo;
    };

    /**
     * O `nao_aplica` sem os campos que, AGORA, são obrigatórios.
     *
     * A marca é gravada pelo caminho do campo e sobrevive a tudo — inclusive a deixar de ser
     * permitida, quando a obrigatoriedade depende de outra resposta. Ele marca "não se aplica"
     * na inscrição estadual na seção 1 e, na seção 4, escolhe um tipo de fornecedor que a exige.
     * O badge some e o asterisco aparece — o `CampoNA` resolve `!obrigatorio && marcados.includes()`,
     * que ESCONDE o efeito sem tirar a chave da lista.
     *
     * Sem este filtro o desfecho é o pior possível: a tela exige o campo, ele digita, o
     * `semCamposNaoAplicaveis` apaga o que ele digitou, e o revisor lê "marcado como não aplicável"
     * sobre um campo preenchido. Perda silenciosa dos dois lados.
     *
     * NCM e NBS ficam de fora de propósito: nenhum dos dois é obrigatório sozinho — basta um —,
     * então marcar o que não se tem é resposta legítima.
     */
    const naoAplicaValido = () => {
        const tipo = dados?.fiscal?.tipo_fornecedor;
        const emitidos = dados?.fiscal?.documentos_fiscais || [];

        const exigidosAgora = [
            ...OBRIGATORIOS,
            ...(TIPOS_COM_MERCADORIA.includes(tipo) ? ['inscricaoestadual'] : []),
            ...(TIPOS_COM_SERVICO.includes(tipo) ? ['inscricaomunicipal'] : []),
            ...(emitidos.includes('outro') ? ['fiscal.documentos_fiscais_outro'] : []),
            // Complementos que a resposta anterior abre, e o par do PIX: nessas condições são
            // obrigatórios e ficam sem N/A na tela (ver o `obrigatorio` de cada `CampoNA`).
            ...(['imune_isento', 'outro'].includes(dados?.fiscal?.regime_irpj) ? ['fiscal.regime_irpj_fundamento'] : []),
            ...(dados?.fiscal?.regime_especifico === 'outro' ? ['fiscal.regime_especifico_outro'] : []),
            ...(dados?.fiscal?.diferimento_aplica === 'sim' ? ['fiscal.diferimento_suspensao'] : []),
            ...(pixObrigatorio('pix_tipo') ? ['banco.pix_tipo'] : []),
            ...(pixObrigatorio('pix_chave') ? ['banco.pix_chave'] : []),
            // Documento sem N/A na tela — o "Outro", o exigido e o que já tem arquivo — não
            // viaja como "não se aplica", nem que a chave tenha sobrado de uma rodada antiga.
            'documentos.outro',
            ...documentosExigidos().map(tipoDoc => 'documentos.' + tipoDoc),
            ...documentos.map(doc => 'documentos.' + doc.tipo),
        ];

        return (dados?.nao_aplica || []).filter(caminho => !exigidosAgora.includes(caminho));
    };


    /**
     * A pergunta foi marcada como "não se aplica"?
     *
     * Serve para tirar a exigência da conferência do Enviar. "Não se aplica" é uma
     * resposta, então quem cede é o obrigatório — senão marcar N/A num campo
     * exigido travava o envio. O servidor faz a mesma coisa, porque a conferência
     * do clique é conveniência e não garantia.
     */
    const contextoNA = { marcados: dados?.nao_aplica || [], alternar: alternarNaoAplica };

    const naoSeAplica = (rotulo) => (dados?.nao_aplica || []).includes(rotulo);

    /**
     * Par do PIX: um lado é obrigatório, e fica sem N/A, quando o outro está respondido —
     * preenchido e sem a marca. Olhar só o valor do outro deixava a chave exigida depois
     * de o tipo ser marcado N/A, porque marcar não apaga o que já estava escolhido.
     */
    const pixObrigatorio = (lado) => {
        const outro = lado === 'pix_tipo' ? 'pix_chave' : 'pix_tipo';
        return !!dados?.banco?.[outro] && !naoSeAplica('banco.' + outro);
    };

    /** Valor de um caminho (`banco.agencia`, `endereco.cep`) dentro do estado. */
    const valorDe = (caminho) => String(caminho).split('.').reduce(
        (no, parte) => (no && typeof no === 'object' ? no[parte] : undefined),
        dados
    );

    /**
     * O fornecedor é Simples Nacional? Decide a certidão do Simples.
     *
     * Olha os DOIS regimes: o de IRPJ/CSLL (5.1) e o de apuração de IBS/CBS (5.2).
     * Quem optou pelo regime regular de IBS/CBS continua no Simples para o IRPJ.
     */
    const ehSimplesNacional = () => dados.fiscal.regime_irpj === 'simples'
        || ['simples_nacional', 'simples_regime_regular'].includes(dados.fiscal.regime_ibs_cbs);

    /**
     * A 5.2 só é pergunta para o optante do Simples, com as duas formas da área. Para os demais, o
     * regime de IBS/CBS sai do regime de IRPJ: MEI é `mei`, o resto é `regular`. Trocar o regime de
     * IRPJ refaz a conta, para a vigência e os anexos não seguirem uma forma do Simples que não vale mais.
     */
    const setRegimeIrpj = (valor) => setDados(d => {
        const atual = d.fiscal.regime_ibs_cbs;
        const ibsCbs = valor === 'simples'
            ? (FORMAS_SIMPLES.includes(atual) ? atual : '')
            : (valor === 'mei' ? 'mei' : (valor ? 'regular' : ''));
        return { ...d, fiscal: { ...d.fiscal, regime_irpj: valor, regime_ibs_cbs: ibsCbs } };
    });

    /**
     * Os anexos exigidos agora — a mesma conta do servidor.
     *
     * A certidão do Simples vale para todo optante. O comprovante da opção pelo regime
     * regular, só para quem fez essa opção: o Simples puro não tem esse documento, e
     * exigi-lo dele travava o envio sem saída.
     */
    const documentosExigidos = () => [
        ...DOCUMENTOS_OBRIGATORIOS,
        ...(ehSimplesNacional() ? ['certidao_simples'] : []),
        ...(dados.fiscal.regime_ibs_cbs === 'simples_regime_regular' ? ['opcao_regime_regular'] : []),
    ];

    /**
     * O que ainda impede o envio, um item por campo.
     *
     * Percorre `OBRIGATORIOS` e mais as regras que dependem de outra resposta. Só o par
     * NCM/NBS consulta `nao_aplica`, porque cada um tem N/A e basta um dos dois. Os
     * demais não: campo obrigatório não tem N/A, e uma chave que tenha sobrado no
     * `nao_aplica` de uma submissão antiga — de quando ele ainda era opcional — não
     * pode liberar o envio agora.
     *
     * Cada item traz o `campo` — o `data-campo` da tela, que para quem está num
     * `CampoNA` é o próprio caminho — e, quando a regra não é só presença, a `mensagem`.
     * O botão não trava por isto: quem usa a lista é o `tentarEnviar`, no clique.
     */
    const pendencias = () => {
        const vazio = (valor) => valor === null || valor === undefined || valor === ''
            || (Array.isArray(valor) && valor.length === 0);
        const lista = [];
        const falta = (campo, mensagem = null) => lista.push({ campo, mensagem });

        OBRIGATORIOS.forEach(caminho => {
            if (vazio(valorDe(caminho))) falta(caminho);
        });

        // Formato, além de presença: CNPJ ou CPF que não fecha o dígito verificador
        // continua sendo recusado pelo servidor. O do favorecido é obrigatório, e o anexo
        // manda compará-lo com o emitente da nota — comparar com lixo digitado não é
        // comparação.
        if (!vazio(dados.cnpj) && !cnpjValido(dados.cnpj)) {
            falta('cnpj', 'CNPJ inválido: confira os números');
        }
        if (!vazio(dados.banco.cnpj_favorecido) && !cnpjValido(dados.banco.cnpj_favorecido)) {
            falta('banco.cnpj_favorecido', 'CNPJ do favorecido inválido: confira os números');
        }
        if (!vazio(dados.assinante_cpf) && !cpfValido(dados.assinante_cpf)) {
            falta('assinante_cpf', 'CPF do representante inválido: confira os números');
        }

        // Documentos fiscais emitidos: array, e "nenhum marcado" não é resposta —
        // todo fornecedor emite alguma coisa, nem que seja recibo/RPA.
        const emitidos = dados.fiscal.documentos_fiscais || [];
        if (emitidos.length === 0) {
            falta('fiscal.documentos_fiscais', 'Marque pelo menos um documento fiscal que você emite');
        }

        // "Outro" marcado sem dizer qual é meio caminho: o revisor recebe a
        // afirmação de que existe um documento a mais e nenhuma pista de qual.
        if (emitidos.includes('outro') && vazio(dados.fiscal.documentos_fiscais_outro)) {
            falta('fiscal.documentos_fiscais_outro');
        }

        // Inscrições, pelo tipo de fornecedor. Quem só presta serviço não tem
        // estadual, e exigir a dele trava o cadastro por um dado que não existe.
        const tipo = dados.fiscal.tipo_fornecedor;
        if (TIPOS_COM_MERCADORIA.includes(tipo) && vazio(dados.inscricaoestadual)) {
            falta('inscricaoestadual');
        }
        if (TIPOS_COM_SERVICO.includes(tipo) && vazio(dados.inscricaomunicipal)) {
            falta('inscricaomunicipal');
        }

        // NCM ou NBS: um basta. NCM é de mercadoria, NBS é de serviço, e exigir os
        // dois obrigaria quem só faz um dos lados a inventar o outro. O marcado como N/A
        // conta como vazio, porque o envio o tira do corpo, e a pendência aponta para o
        // que ficou sem marca, não para o campo travado pelo N/A.
        const respondido = (caminho) => !naoSeAplica(caminho) && !vazio(valorDe(caminho));
        if (!respondido('fiscal.ncm') && !respondido('fiscal.nbs')) {
            const ambosNA = naoSeAplica('fiscal.ncm') && naoSeAplica('fiscal.nbs');
            falta(
                naoSeAplica('fiscal.ncm') && !ambosNA ? 'fiscal.nbs' : 'fiscal.ncm',
                ambosNA ? 'NCM e NBS estão como não se aplica: desmarque um deles e preencha' : 'Preencha o NCM ou o NBS'
            );
        }

        // Complementos que só se exigem quando a resposta anterior abre a pergunta.
        if (['imune_isento', 'outro'].includes(dados.fiscal.regime_irpj) && vazio(dados.fiscal.regime_irpj_fundamento)) {
            falta('fiscal.regime_irpj_fundamento');
        }
        if (dados.fiscal.regime_especifico === 'outro' && vazio(dados.fiscal.regime_especifico_outro)) {
            falta('fiscal.regime_especifico_outro');
        }
        if (possuiReducao && vazio(dados.fiscal.reducao_percentual)) {
            falta('fiscal.reducao_percentual', 'Informe qual é o benefício e o fundamento legal');
        }
        if (dados.fiscal.diferimento_aplica === 'sim' && vazio(dados.fiscal.diferimento_suspensao)) {
            falta('fiscal.diferimento_suspensao');
        }

        // A seção de contatos não tem N/A e é opcional inteira, MENOS o responsável
        // por CBS/IBS: é quem responde pelo que este formulário coleta, e sem ele o
        // cadastro fica sem a quem recorrer depois.
        const indiceResponsavel = (dados.contatos || []).findIndex(c => c.cargo === 'cbs_ibs');
        if (indiceResponsavel < 0) {
            falta('contatos', 'Adicione o contato Responsável por CBS/IBS');
        } else {
            const responsavel = dados.contatos[indiceResponsavel];
            [['nome', 'Nome'], ['email', 'E-mail'], ['celular', 'Telefone']].forEach(([campo, rotulo]) => {
                if (vazio(responsavel[campo])) {
                    falta(`contatos.${indiceResponsavel}.${campo}`, `Preencha o ${rotulo} do Responsável por CBS/IBS`);
                }
            });
        }

        // Par do PIX — um sem o outro é declaração pela metade. Segue opcional: os dois
        // só se exigem mutuamente.
        // O lado marcado N/A, e que não virou obrigatório, conta como vazio: o envio o tira.
        const pixRespondido = (lado) => !vazio(dados.banco[lado])
            && (pixObrigatorio(lado) || !naoSeAplica('banco.' + lado));
        if (pixRespondido('pix_chave') !== pixRespondido('pix_tipo')) {
            falta(pixRespondido('pix_chave') ? 'banco.pix_tipo' : 'banco.pix_chave', 'Chave PIX e tipo vão juntos: preencha os dois ou nenhum');
        }

        const anexados = (documentos || []).map(d => d.tipo);
        documentosExigidos().forEach(tipoDoc => {
            if (!anexados.includes(tipoDoc)) {
                const rotulo = (TIPOS_DOCUMENTO.find(t => t.valor === tipoDoc) || {}).rotulo || tipoDoc;
                falta('documentos.' + tipoDoc, `Anexe o documento obrigatório: ${rotulo}`);
            }
        });

        // Seção 10: TODAS as declarações. Aceite parcial não é aceite — as cinco são
        // condição da relação comercial, e o servidor recusa do mesmo jeito.
        const semAceite = (contexto?.declaracoes || []).find(d => !(dados.aceite || []).includes(d.chave));
        if (semAceite) {
            falta('aceite.' + semAceite.chave, 'Marque todas as declarações da seção 10');
        }

        return lista;
    };

    /**
     * Clique do Enviar.
     *
     * O botão não trava mais pela validação: travado, ele não dizia o que faltava, e
     * quem achava ter preenchido tudo ficava sem saída. A conferência roda aqui, avisa
     * o primeiro pendente — o de mais acima na página — e leva o cursor até ele.
     */
    const tentarEnviar = () => {
        const lista = pendencias();
        if (lista.length === 0) {
            enviar();
            return;
        }

        // A validação leu o estado; o DOM entra só agora, para achar e focar o campo.
        const comElemento = lista.map(p => ({ ...p, el: document.querySelector(`[data-campo="${p.campo}"]`) }));
        const primeira = comElemento
            .filter(p => p.el)
            .sort((a, b) => (a.el.compareDocumentPosition(b.el) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1))[0]
            || comElemento[0];

        const rotulo = (primeira.el && primeira.el.dataset.rotulo) || primeira.campo;
        toast.error(primeira.mensagem ? `${primeira.mensagem}.` : `Preencha o campo obrigatório: ${rotulo}.`);

        if (primeira.el) {
            // O badge de N/A fica no rótulo, antes do campo: fora da busca. O input de
            // arquivo é escondido; no anexo, o foco vai para o botão Anexar.
            const seletor = 'input:not([type="hidden"]):not([type="file"]), select, textarea, button:not(.fin-pp__minitag), label.fin-pp__btn';
            const controle = primeira.el.matches(seletor) ? primeira.el : primeira.el.querySelector(seletor);
            // `display: contents` não tem caixa para rolar até ela: rola até o controle.
            (controle || primeira.el.firstElementChild || primeira.el).scrollIntoView({ behavior: 'smooth', block: 'center' });
            if (controle) controle.focus({ preventScroll: true });
        }
    };


    const enviar = async () => {
        setEnviando(true);
        setErroEnvio(null);
        try {
            // Descarta os contatos que ficaram só com o papel semeado. A tela semeia
            // os quatro papéis do anexo para o fornecedor ver o que falta; enviá-los
            // em branco faria a aprovação gravar quatro contatos vazios no cadastro,
            // que a tela interna renderiza como quatro linhas sem nada.
            // Uma lista só, usada nos dois lugares: o que viaja como `nao_aplica` e o que é
            // apagado do corpo. Se as duas divergirem, o servidor grava "não se aplica" sobre um
            // campo que veio preenchido — ou apaga um que não está marcado.
            const marcados = naoAplicaValido();

            const corpo = {
                ...semCamposNaoAplicaveis({
                    ...dados,
                    nao_aplica: marcados,
                    contatos: (dados.contatos || []).filter(c => c.nome || c.email || c.celular),
                    // A versão viaja para o servidor conferir que o texto aceito é o que
                    // está no ar. Se tiver mudado enquanto ele preenchia, o POST volta 409
                    // e a tela pede recarga — melhor que gravar aceite de um texto velho.
                    aceite_versao: contexto?.declaracoesVersao,
                }, marcados),
                // O CNPJ do acesso, sem máscara, e o antigo na troca: o servidor resolve o caminho
                // de novo a partir deles.
                cnpj: acesso.cnpj,
                ...(acesso.cnpjAntigo ? { cnpj_antigo: acesso.cnpjAntigo } : {}),
            };

            // CEP só com os números: a máscara deixa `-` e `_` no texto, e o servidor confere
            // os 8 dígitos. O campo na tela continua mascarado.
            if (corpo.endereco) {
                corpo.endereco = { ...corpo.endereco, cep: String(corpo.endereco.cep || '').replace(/\D/g, '') };
            }

            // Redução desmarcada é "não possui": o texto que tenha ficado na tela não vai.
            if (!possuiReducao && corpo.fiscal) {
                corpo.fiscal = { ...corpo.fiscal, reducao_percentual: '' };
            }

            // Multipart por causa dos anexos: os campos num JSON só, em `dados` — o back o
            // abre e lê o resto como sempre —, e cada arquivo ao lado, com o tipo dele.
            // A partir de 30 MB o POST passaria do teto do Cloud Run: vão só os tipos, para o
            // back conferir os obrigatórios, e os arquivos sobem depois, um por requisição.
            const depois = tamanhoAnexos >= TAMANHO_TOTAL_ANEXOS;
            const form = new FormData();
            form.append('dados', JSON.stringify(corpo));
            documentos.forEach((doc, indice) => {
                form.append(`documentos[${indice}][tipo]`, doc.tipo);
                if (!depois) form.append(`documentos[${indice}][arquivo]`, doc.arquivo);
            });
            if (depois) form.append('anexos_depois', '1');

            const { data } = await axios.post(`${API}/public/cadastro-fornecedor/${token}`, form);
            setEnviado(true);
            if (depois) subirAnexos(data.anexos, documentos.map(doc => ({ ...doc, ok: false })));
        } catch (e) {
            setErroEnvio(e.response?.data?.mensagem || 'Não foi possível enviar. Tente novamente.');
        } finally {
            setEnviando(false);
        }
    };

    // Sobe, um por vez, os anexos da fila que ainda não foram. O "Tentar de novo" chama de novo
    // com a mesma fila, e só os que falharam voltam a subir.
    const subirAnexos = async (link, fila) => {
        const pendentes = fila.filter(doc => !doc.ok);
        let atualizada = fila;
        let erroUltimo = null;

        for (let i = 0; i < pendentes.length; i++) {
            setAnexosDepois({ link, fila: atualizada, atual: i + 1, total: pendentes.length, subindo: true, erro: null });
            try {
                const form = new FormData();
                form.append('tipo', pendentes[i].tipo);
                form.append('arquivo', pendentes[i].arquivo);
                await axios.post(`${API}${link}`, form);
                atualizada = atualizada.map(doc => (doc.chave === pendentes[i].chave ? { ...doc, ok: true } : doc));
            } catch (e) {
                erroUltimo = e.response?.data?.mensagem || 'Não foi possível enviar o arquivo.';
            }
        }

        setAnexosDepois({ link, fila: atualizada, atual: 0, total: 0, subindo: false, erro: erroUltimo });
    };


    // Passo do CNPJ. O servidor diz se o CNPJ tem cadastro (atualização) e, sem cadastro, se o CNPJ
    // antigo informado tem (troca de CNPJ); quem não era fornecedor segue como cadastro novo. Ele
    // nunca diz de quem é o CNPJ, e a tela também não mostra.
    const consultarCnpj = async (comAntigo = false) => {
        setConsultando(true);
        setErroCnpj(null);
        setAvisoAntigo(null);
        try {
            const { data } = await axios.post(`${API}/public/cadastro-fornecedor/${token}/cnpj`, {
                cnpj: cnpjDigitado,
                ...(comAntigo ? { cnpj_antigo: cnpjAntigo } : {}),
            });
            return data.dados;
        } catch (e) {
            // 429 é o limite por IP da rota, e a mensagem dele vem do Laravel, em inglês.
            setErroCnpj(e.response?.status === 429
                ? 'Muitas consultas seguidas. Aguarde um minuto e tente de novo.'
                : (e.response?.data?.mensagem || 'Não foi possível consultar o CNPJ. Tente de novo.'));
            return null;
        } finally {
            setConsultando(false);
        }
    };

    // Abre o formulário para o CNPJ do acesso, em branco de novo a cada CNPJ: nada do que foi
    // digitado ou anexado para um passa para o outro.
    const abrirFormulario = (resultado, caminho) => {
        setAcesso({ ...resultado, caminho, cnpjAntigo: caminho === 'troca_cnpj' ? limparCnpj(cnpjAntigo) : null });
        setDados({ ...cadastroEmBranco(contexto?.paises), cnpj: formatarCnpj(resultado.cnpj) });
        setDocumentos([]);
        setErroAnexo(null);
        setPossuiReducao(false);
        setErroEnvio(null);
    };

    const continuarCnpj = async () => {
        const resultado = await consultarCnpj();
        if (!resultado) return;
        if (resultado.encontrado) {
            abrirFormulario(resultado, 'atualizacao');
            return;
        }
        setConsulta(resultado);
        setJaEra('');
        setCnpjAntigo('');
    };

    const continuarJaEra = async () => {
        if (jaEra === 'nao') {
            abrirFormulario(consulta, 'novo');
            return;
        }
        const resultado = await consultarCnpj(true);
        if (!resultado) return;
        if (resultado.antigoEncontrado) {
            abrirFormulario(resultado, 'troca_cnpj');
            return;
        }
        setAvisoAntigo('Não encontramos o CNPJ antigo. Confira o número, ou escolha "Não, é meu primeiro cadastro" para seguir com um cadastro novo.');
    };

    // "Trocar CNPJ": volta ao passo do CNPJ; o próximo abre o formulário em branco.
    const trocarCnpj = () => {
        setAcesso(null);
        setConsulta(null);
        setCnpjDigitado('');
        setJaEra('');
        setCnpjAntigo('');
        setErroCnpj(null);
        setAvisoAntigo(null);
        setErroEnvio(null);
    };

    // Depois de um envio: o mesmo link serve a outro CNPJ, como o de outra filial da mesma rede.
    const preencherOutroCnpj = () => {
        setEnviado(false);
        setAnexosDepois(null);
        trocarCnpj();
    };

    const antigoIgualAoNovo = !!consulta && !!cnpjAntigo && limparCnpj(cnpjAntigo) === limparCnpj(consulta.cnpj);
    const podeContinuarJaEra = jaEra === 'nao'
        || (jaEra === 'sim' && cnpjValido(cnpjAntigo) && !antigoIgualAoNovo);

    // O que aprovar vai fazer com o cadastro, dito a quem preenche antes de começar.
    const explicacaoDoCaminho = !acesso ? '' : ({
        atualizacao: 'Depois da conferência da Access, os dados enviados substituem os do cadastro deste CNPJ.',
        novo: 'Depois da conferência da Access, um cadastro novo é criado com este CNPJ.',
        troca_cnpj: `Depois da conferência da Access, o cadastro do CNPJ ${formatarCnpj(acesso.cnpjAntigo)} é desativado e este é criado no lugar.`,
    })[acesso.caminho] || '';

    if (carregando) {
        return <Moldura><div className="fin-pp__card" style={{ padding: 40, textAlign: 'center' }}>Carregando…</div></Moldura>;
    }

    if (erro) {
        return (
            <Moldura>
                <div className="fin-pp__card" style={{ padding: 32, textAlign: 'center' }}>
                    <FaExclamationTriangle size={34} style={{ color: 'var(--fin-wn)', marginBottom: 12 }} />
                    <h2 className="fin-pp__title" style={{ fontSize: 20 }}>Link indisponível</h2>
                    <p style={{ color: 'var(--fin-mt)', marginTop: 8 }}>{erro}</p>
                </div>
            </Moldura>
        );
    }

    // Rejeitada NÃO cai aqui: o backend devolve `jaSubmetido = false` nesse caso e o
    // formulário abre de novo, com o motivo num banner. O ciclo de correção usa o
    // MESMO link — antes esta tela prometia "entraremos em contato com um novo link"
    // que nenhum código emitia, e o fornecedor ficava esperando para sempre.
    // O link não se esgota no envio: a trava é por CNPJ, e ele serve a outro.
    if (enviado || acesso?.jaSubmetido) {
        return (
            <Moldura>
                <div className="fin-pp__card" style={{ padding: 32, textAlign: 'center' }}>
                    <FaCheckCircle size={34} style={{ color: 'var(--fin-ok)', marginBottom: 12 }} />
                    <h2 className="fin-pp__title" style={{ fontSize: 20 }}>Cadastro recebido</h2>
                    {acesso && (
                        <p style={{ color: 'var(--fin-tx)', fontWeight: 700, marginTop: 8 }}>CNPJ {formatarCnpj(acesso.cnpj)}</p>
                    )}
                    <p style={{ color: 'var(--fin-mt)', marginTop: 8 }}>
                        Nossa equipe vai conferir os dados e retornar se faltar algo. Não é preciso enviar de novo.
                    </p>
                    {anexosDepois?.subindo && (
                        <div className="fin-pp__banner is-info" style={{ marginTop: 16, textAlign: 'left' }}>
                            <span>
                                <strong>Enviando arquivos ({anexosDepois.atual} de {anexosDepois.total})</strong>.
                                Não feche esta página até terminar.
                            </span>
                        </div>
                    )}
                    {anexosDepois && !anexosDepois.subindo && anexosDepois.fila.some(doc => !doc.ok) && (
                        <div className="fin-pp__banner is-warn" style={{ marginTop: 16, textAlign: 'left', flexWrap: 'wrap', gap: 8 }}>
                            <span style={{ flex: 1, minWidth: 0 }}>
                                <strong>Alguns arquivos não foram enviados:</strong>{' '}
                                {anexosDepois.fila.filter(doc => !doc.ok).map(doc => doc.arquivo.name).join(', ')}.
                                {anexosDepois.erro ? ` ${anexosDepois.erro}` : ''}
                            </span>
                            <button
                                type="button"
                                className="fin-pp__btn fin-pp__btn--sm"
                                onClick={() => subirAnexos(anexosDepois.link, anexosDepois.fila)}
                            >
                                Tentar de novo
                            </button>
                        </div>
                    )}
                    <button
                        type="button"
                        className="fin-pp__btn fin-pp__btn--primary"
                        style={{ marginTop: 16 }}
                        disabled={!!anexosDepois?.subindo}
                        onClick={enviado ? preencherOutroCnpj : trocarCnpj}
                    >
                        Preencher outro CNPJ
                    </button>
                </div>
            </Moldura>
        );
    }

    if (!acesso) {
        return (
            <Moldura>
                <div className="fin-pp__head" style={{ marginBottom: 16 }}>
                    <div>
                        <h1 className="fin-pp__title">Cadastro de fornecedor</h1>
                    </div>
                </div>

                <div className="fin-pp__card" style={{ marginBottom: 16, padding: '18px 20px' }}>
                    {!consulta ? (
                        <>
                            <div className="fin-pp__cphead" style={{ marginBottom: 6 }}>
                                <span className="fin-pp__cphead-title">Informe o CNPJ da sua empresa</span>
                            </div>
                            <p style={{ fontSize: 13, color: 'var(--fin-mt)', margin: '0 0 14px', lineHeight: 1.6 }}>
                                Digite o CNPJ de quem emite as notas para a Access; o formulário aparece em seguida.
                            </p>
                            <div className="fin-pp__filters">
                                <FinField label="CNPJ" required>
                                    <FinInput
                                        value={cnpjDigitado}
                                        placeholder="00.000.000/0000-00"
                                        maxLength={18}
                                        onChange={e => { setCnpjDigitado(formatarCnpj(e.target.value)); setErroCnpj(null); }}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' && cnpjValido(cnpjDigitado) && !consultando) continuarCnpj();
                                        }}
                                    />
                                    {limparCnpj(cnpjDigitado).length === 14 && !cnpjValido(cnpjDigitado) && (
                                        <span style={{ fontSize: 12, color: 'var(--fin-dg)', display: 'block', marginTop: 4 }}>
                                            CNPJ inválido — o dígito verificador não bate.
                                        </span>
                                    )}
                                </FinField>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="fin-pp__cphead" style={{ marginBottom: 6 }}>
                                <span className="fin-pp__cphead-title">Não encontramos este CNPJ</span>
                            </div>
                            <p style={{ fontSize: 13, color: 'var(--fin-mt)', margin: '0 0 14px', lineHeight: 1.6 }}>
                                CNPJ informado: <strong style={{ color: 'var(--fin-tx)' }}>{formatarCnpj(consulta.cnpj)}</strong>
                            </p>
                            <div className="fin-pp__filters">
                                {/* "Marque um": escolha única, radio. */}
                                <FinField label="Você já era fornecedor da Access?" required full>
                                    <FinChecks inline>
                                        <FinCheck
                                            id="ja_era_sim"
                                            type="radio"
                                            name="ja_era"
                                            label="Sim, com outro CNPJ"
                                            checked={jaEra === 'sim'}
                                            onChange={() => { setJaEra('sim'); setAvisoAntigo(null); setErroCnpj(null); }}
                                        />
                                        <FinCheck
                                            id="ja_era_nao"
                                            type="radio"
                                            name="ja_era"
                                            label="Não, é meu primeiro cadastro"
                                            checked={jaEra === 'nao'}
                                            onChange={() => { setJaEra('nao'); setAvisoAntigo(null); setErroCnpj(null); }}
                                        />
                                    </FinChecks>
                                </FinField>
                                {jaEra === 'sim' && (
                                    <FinField label="CNPJ antigo" required>
                                        <FinInput
                                            value={cnpjAntigo}
                                            placeholder="00.000.000/0000-00"
                                            maxLength={18}
                                            onChange={e => { setCnpjAntigo(formatarCnpj(e.target.value)); setAvisoAntigo(null); setErroCnpj(null); }}
                                        />
                                        {limparCnpj(cnpjAntigo).length === 14 && !cnpjValido(cnpjAntigo) && (
                                            <span style={{ fontSize: 12, color: 'var(--fin-dg)', display: 'block', marginTop: 4 }}>
                                                CNPJ inválido — o dígito verificador não bate.
                                            </span>
                                        )}
                                        {antigoIgualAoNovo && (
                                            <span style={{ fontSize: 12, color: 'var(--fin-dg)', display: 'block', marginTop: 4 }}>
                                                O CNPJ antigo precisa ser diferente do novo.
                                            </span>
                                        )}
                                    </FinField>
                                )}
                            </div>
                            {jaEra === 'sim' && (
                                <p style={{ fontSize: 12, color: 'var(--fin-mt)', margin: '8px 0 0' }}>
                                    Depois da conferência da Access, o cadastro do CNPJ antigo é desativado e um novo é
                                    criado com o CNPJ {formatarCnpj(consulta.cnpj)}.
                                </p>
                            )}
                        </>
                    )}

                    {avisoAntigo && (
                        <div className="fin-pp__banner is-warn" style={{ marginTop: 12 }}>{avisoAntigo}</div>
                    )}
                    {erroCnpj && (
                        <div className="fin-pp__banner is-warn" style={{ marginTop: 12 }}>{erroCnpj}</div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
                        {consulta && (
                            <button type="button" className="fin-pp__btn" onClick={trocarCnpj}>
                                Trocar CNPJ
                            </button>
                        )}
                        <button
                            type="button"
                            className="fin-pp__btn fin-pp__btn--primary"
                            disabled={consultando || (consulta ? !podeContinuarJaEra : !cnpjValido(cnpjDigitado))}
                            onClick={consulta ? continuarJaEra : continuarCnpj}
                        >
                            {consultando ? 'Consultando…' : 'Continuar'}
                        </button>
                    </div>
                </div>
            </Moldura>
        );
    }

    return (
        <NaoAplicaContext.Provider value={contextoNA}>
        <Moldura>
            <div className="fin-pp__head" style={{ marginBottom: 16 }}>
                <div>
                    <h1 className="fin-pp__title">Cadastro de fornecedor</h1>
                    <p className="fin-pp__subtitle">
                        Preencha os dados da sua empresa. Por segurança o formulário abre em branco — não exibimos aqui os dados que você já nos informou.
                    </p>
                </div>
            </div>

            {/* Amostra: a tela abre para o time interno conferir que o link funciona,
                mas o POST é recusado no servidor. Avisar aqui evita que alguém
                preencha 30 campos e só descubra no envio. */}
            {contexto?.amostra && (
                <div className="fin-pp__banner is-info" style={{ marginBottom: 16 }}>
                    <span>
                        <strong>Amostra interna.</strong> Esta é uma cópia de teste do formulário —
                        o envio está desativado.
                    </span>
                </div>
            )}

            {acesso.statusRevisao === 'rejeitada' && (
                <div className="fin-pp__banner is-warn" style={{ marginBottom: 16 }}>
                    <span>
                        <strong>Precisamos de um ajuste.</strong>{' '}
                        {acesso.motivoRejeicao || 'Confira os dados e envie novamente.'}
                    </span>
                </div>
            )}

            <div
                className="fin-pp__card"
                style={{ marginBottom: 16, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
            >
                <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12, color: 'var(--fin-mt)' }}>Preenchendo para</div>
                    <div style={{ fontWeight: 700, color: 'var(--fin-tx)' }}>
                        CNPJ {formatarCnpj(acesso.cnpj)}
                        <span className={'fin-pp__pill ' + (PILL_CAMINHO[acesso.caminho] || 'is-info')} style={{ marginLeft: 8 }}>
                            {rotuloDe(CAMINHOS_SUBMISSAO, acesso.caminho)}
                        </span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--fin-mt)' }}>{explicacaoDoCaminho}</div>
                </div>
                <button type="button" className="fin-pp__btn fin-pp__btn--sm" onClick={trocarCnpj}>
                    Trocar CNPJ
                </button>
            </div>

            <div className="fin-pp__card" style={{ marginBottom: 16, padding: '18px 20px' }}>
                <div className="fin-pp__cphead" style={{ marginBottom: 14 }}>
                    <span className="fin-pp__cphead-title">Identificação da empresa</span>
                </div>
                {/* Cabeçalho do anexo. Fica aqui e não num card próprio porque é uma
                    pergunta só, e um card inteiro para ela empurraria o formulário
                    para baixo sem acrescentar informação. */}
                <div className="fin-pp__filters" style={{ marginBottom: 10 }}>
                    <CampoNA naKey="natureza" label="Natureza do cadastro" grow obrigatorio>
                        <ReactSelect
                            options={opcoesDe(NATUREZAS_CADASTRO)}
                            value={dados.natureza ? { value: dados.natureza } : null}
                            onChange={e => set('natureza', e ? e.value : '')}
                        />
                    </CampoNA>
                </div>
                <div className="fin-pp__filters">
                    <CampoNA naKey="razaosocial" label="Razão social" full obrigatorio>
                        <FinInput value={dados.razaosocial} onChange={e => set('razaosocial', e.target.value)} />
                    </CampoNA>
                    <CampoNA naKey="nomefantasia" label="Nome fantasia" grow>
                        <FinInput value={dados.nomefantasia} onChange={e => set('nomefantasia', e.target.value)} />
                    </CampoNA>
                    <CampoNA naKey="cnpj" label="CNPJ" required obrigatorio>
                        {/* Vem do passo do CNPJ e não se edita aqui: é ele que decide o cadastro. Para
                            mudar, é o "Trocar CNPJ" do topo, que abre o formulário em branco de novo. */}
                        <FinInput
                            value={dados.cnpj}
                            readOnly
                            title="Para mudar o CNPJ, use Trocar CNPJ no topo do formulário"
                            style={{ background: 'var(--fin-sf2)' }}
                        />
                        {/* Só reclama depois que ele digitou algo — acusar "inválido"
                            num campo vazio que ele acabou de focar é ruído. */}
                        {dados.cnpj && !cnpjValido(dados.cnpj) && (
                            <span style={{ fontSize: 12, color: 'var(--fin-dg)', display: 'block', marginTop: 4 }}>
                                CNPJ inválido — o dígito verificador não bate.
                            </span>
                        )}
                    </CampoNA>
                    <CampoNA naKey="inscricaoestadual" label="Inscrição estadual" obrigatorio={TIPOS_COM_MERCADORIA.includes(dados.fiscal.tipo_fornecedor)}>
                        <FinInput value={dados.inscricaoestadual} onChange={e => set('inscricaoestadual', e.target.value)} />
                    </CampoNA>
                    <CampoNA naKey="inscricaomunicipal" label="Inscrição municipal" obrigatorio={TIPOS_COM_SERVICO.includes(dados.fiscal.tipo_fornecedor)}>
                        <FinInput value={dados.inscricaomunicipal} onChange={e => set('inscricaomunicipal', e.target.value)} />
                    </CampoNA>
                    {/* CNAE declarado pelo fornecedor. Os mesmos códigos vindos da
                        Receita ficam em `fornecedor_cnae`; a comparação entre os dois
                        roda depois, pela API que já existe — não aqui. Texto livre pelo
                        mesmo motivo do NCM: é declaração dele, não chave que resolvemos
                        contra domínio nosso. */}
                    <CampoNA naKey="cnae_principal" label="CNAE principal">
                        <FinInput
                            placeholder="Ex.: 82.30-0-01"
                            value={dados.cnae_principal}
                            onChange={e => set('cnae_principal', e.target.value)}
                        />
                    </CampoNA>
                    <CampoNA naKey="cnaes_secundarios" label="CNAEs secundários" full>
                        <FinTextarea
                            rows={2}
                            placeholder="Um código por linha, ou separados por vírgula"
                            value={dados.cnaes_secundarios}
                            onChange={e => set('cnaes_secundarios', e.target.value)}
                        />
                    </CampoNA>
                    <CampoNA naKey="porte" label="Porte da empresa">
                        <ReactSelect
                            options={opcoesDe(PORTES_EMPRESA)}
                            placeholder={placeholderDe(PORTES_EMPRESA)}
                            isClearable
                            value={dados.porte ? { value: dados.porte } : null}
                            onChange={e => set('porte', e ? e.value : '')}
                        />
                    </CampoNA>
                    <CampoNA naKey="data_constituicao" label="Data de constituição">
                        <FinInput
                            type="date"
                            value={dados.data_constituicao || ''}
                            onChange={e => set('data_constituicao', e.target.value)}
                        />
                    </CampoNA>
                </div>

            </div>

            <div className="fin-pp__card" style={{ marginBottom: 16, padding: '18px 20px' }}>
                <div className="fin-pp__cphead" style={{ marginBottom: 14 }}>
                    <span className="fin-pp__cphead-title">Endereço</span>
                </div>
                <div className="fin-pp__filters">
                    <CampoNA naKey="endereco.cep" label="CEP" obrigatorio>
                        <InputMask
                            mask="99999-999"
                            className="fin-pp__input"
                            style={{ paddingLeft: 14 }}
                            value={dados.endereco.cep}
                            onChange={e => setEndereco('cep', e.target.value)}
                        />
                    </CampoNA>
                    <CampoNA naKey="endereco.logradouro" label="Logradouro" grow obrigatorio>
                        <FinInput value={dados.endereco.logradouro} onChange={e => setEndereco('logradouro', e.target.value)} />
                    </CampoNA>
                    <CampoNA naKey="endereco.numero" label="Número" obrigatorio>
                        <FinInput value={dados.endereco.numero} onChange={e => setEndereco('numero', e.target.value)} />
                    </CampoNA>
                    <CampoNA naKey="endereco.complemento" label="Complemento">
                        <FinInput value={dados.endereco.complemento} onChange={e => setEndereco('complemento', e.target.value)} />
                    </CampoNA>
                    <CampoNA naKey="endereco.bairro" label="Bairro" grow obrigatorio>
                        <FinInput value={dados.endereco.bairro} onChange={e => setEndereco('bairro', e.target.value)} />
                    </CampoNA>
                    <CampoNA naKey="endereco.uf" label="UF" obrigatorio>
                        <FinInput maxLength={2} value={dados.endereco.uf} onChange={e => setEndereco('uf', e.target.value.toUpperCase())} />
                    </CampoNA>
                    <CampoNA naKey="endereco.pais" label="País">
                        <ReactSelect
                            options={(contexto?.paises || []).map(p => ({ value: p.id, label: p.nome }))}
                            value={dados.endereco.pais ? { value: dados.endereco.pais } : null}
                            onChange={e => setEndereco('pais', e ? e.value : '')}
                        />
                    </CampoNA>
                    <CampoNA naKey="endereco.municipio" label="Município" full obrigatorio>
                        {/* 5570 municípios: busca sob demanda no endpoint atrás do token. */}
                        <ReactSelect
                            isClearable
                            placeholder="Digite ao menos duas letras"
                            value={dados.endereco.municipio
                                ? { value: dados.endereco.municipio.id, label: `${dados.endereco.municipio.nome} — ${dados.endereco.municipio.uf}` }
                                : null}
                            onChange={e => {
                                setEndereco('municipio', e ? e.municipio : null);
                                if (e) setEndereco('uf', e.municipio.uf);
                            }}
                            loadOptions={(input) => {
                                if ((input || '').trim().length < 2) return Promise.resolve([]);
                                return axios.get(`${API}/public/cadastro-fornecedor/${token}/municipios`, { params: { q: input } })
                                    .then(({ data }) => (data.dados || []).map(m => ({ value: m.id, label: `${m.nome} — ${m.uf}`, municipio: m })))
                                    .catch(() => []);
                            }}
                        />
                    </CampoNA>
                </div>
            </div>

            <div className="fin-pp__card" style={{ marginBottom: 16, padding: '18px 20px' }}>
                <div className="fin-pp__cphead" style={{ marginBottom: 14 }}>
                    <span className="fin-pp__cphead-title">Contatos</span>
                    <span className="fin-pp__cphead-sub">comercial, fiscal e financeiro</span>
                </div>
                {/* Esta seção é a única sem marcador de N/A, e por isso precisa dizer
                    em palavras o que o marcador diria: aqui deixar em branco JÁ é a
                    resposta. Sem isso, quem vê quatro linhas com cinco campos assume
                    que a Access espera as vinte respostas. */}
                <p style={{ fontSize: 12, color: 'var(--fin-mt)', margin: '0 0 12px' }}>
                    Preencha só os contatos que existirem, e só os campos que souber — nada
                    aqui é obrigatório. A linha que ficar em branco não é enviada, e a
                    lixeira remove a que você não usa.
                </p>

                {dados.contatos.map((contato, indice) => (
                    <div key={indice} className="fin-pp__filters" style={{ marginBottom: 10, alignItems: 'flex-end' }}>
                        {/* `flex-end` na linha: sem o rótulo em branco que antes empurrava
                            o botão de remover para baixo, é o alinhamento pela base que o
                            mantém na altura dos inputs.

                            Sem N/A nesta seção: contato pode ser parcialmente
                            preenchido — todos os campos são `nullable` no servidor, e o
                            envio descarta a linha que ficou sem nome, e-mail e telefone.
                            Deixar em branco JÁ responde "não tenho este contato", e a
                            lixeira ao lado remove a linha inteira. Um marcador aqui
                            seria uma segunda forma de dizer a mesma coisa. */}
                        <FinField label="Área">
                            <ReactSelect
                                options={opcoesDe(PAPEIS_CONTATO)}
                                value={contato.cargo ? { value: contato.cargo } : null}
                                onChange={e => setContato(indice, 'cargo', e ? e.value : '')}
                            />
                        </FinField>
                        {/* Nome, e-mail e telefone são obrigatórios SÓ no responsável
                            por CBS/IBS. Os outros três papéis seguem opcionais: a área
                            pediu obrigatoriedade apenas desse contato, que é quem
                            responde pelo que este formulário coleta. */}
                        <FinField label="Nome" grow required={contato.cargo === 'cbs_ibs'}>
                            <FinInput data-campo={`contatos.${indice}.nome`} value={contato.nome || ''} onChange={e => setContato(indice, 'nome', e.target.value)} />
                        </FinField>
                        {contato.cargo === 'cbs_ibs' && (
                            <FinField label="Cargo" grow>
                                <FinInput value={contato.funcao || ''} onChange={e => setContato(indice, 'funcao', e.target.value)} />
                            </FinField>
                        )}
                        <FinField label="E-mail" grow required={contato.cargo === 'cbs_ibs'}>
                            <FinInput type="email" data-campo={`contatos.${indice}.email`} value={contato.email || ''} onChange={e => setContato(indice, 'email', e.target.value)} />
                        </FinField>
                        <FinField label="Telefone" required={contato.cargo === 'cbs_ibs'}>
                            <FinInput data-campo={`contatos.${indice}.celular`} value={contato.celular || ''} onChange={e => setContato(indice, 'celular', e.target.value)} />
                        </FinField>
                        {/* Não é campo, é o botão de remover a linha. Dentro de um
                            `FinField` ele herdava `flex: 1 1 200px` e ocupava uma coluna
                            inteira; o `.fin-pp__filters-actions` dá a largura do botão, e
                            o `flex-end` da linha o alinha à base dos inputs. */}
                        {dados.contatos.length > 1 && (
                            <div className="fin-pp__filters-actions">
                                <button
                                    type="button"
                                    title="Remover este contato"
                                    aria-label="Remover este contato"
                                    className="fin-pp__btn fin-pp__btn--danger-ghost fin-pp__btn--icon"
                                    onClick={() => setDados(d => ({ ...d, contatos: d.contatos.filter((_, i) => i !== indice) }))}
                                >
                                    <FaTrash size={13} />
                                </button>
                            </div>
                        )}
                    </div>
                ))}

                <button
                    type="button"
                    className="fin-pp__btn fin-pp__btn--sm"
                    data-campo="contatos"
                    onClick={() => setDados(d => ({ ...d, contatos: [...d.contatos, contatoVazio()] }))}
                >
                    <FaPlus size={12} /> Adicionar contato
                </button>

                <div className="fin-pp__filters" style={{ marginTop: 18 }}>
                    <CampoNA naKey="email_xml" label="E-mail para envio de XML e PDF" grow obrigatorio>
                        <FinInput type="email" value={dados.email_xml} onChange={e => set('email_xml', e.target.value)} />
                    </CampoNA>
                    <CampoNA naKey="prazo_envio_xml" label="Prazo de envio do XML" obrigatorio>
                        <ReactSelect
                            options={opcoesDe(PRAZOS_XML)}
                            placeholder={placeholderDe(PRAZOS_XML)}
                            isClearable
                            value={dados.prazo_envio_xml ? { value: dados.prazo_envio_xml } : null}
                            onChange={e => set('prazo_envio_xml', e ? e.value : '')}
                        />
                    </CampoNA>
                </div>
                <p style={{ fontSize: 12, color: 'var(--fin-mt)', margin: '2px 0 0' }}>
                    O XML é indispensável para a apuração de créditos — o envio apenas do PDF ou DANFE não substitui o arquivo.
                </p>
            </div>

            {/* Visível TAMBÉM na amostra. Escondê-lo fazia sentido enquanto o card vinha
                pré-preenchido e a amostra precisava omitir a conta; agora o formulário
                abre em branco para todo mundo, e esconder faria a amostra mostrar um
                formulário diferente do que o fornecedor vê — que é exatamente o oposto
                do que ela serve para conferir. O envio já está barrado pelo botão. */}
            <div className="fin-pp__card" style={{ marginBottom: 16, padding: '18px 20px' }}>
                <div className="fin-pp__cphead" style={{ marginBottom: 14 }}>
                    <span className="fin-pp__cphead-title">Dados bancários</span>
                    <span className="fin-pp__cphead-sub">a conta deve pertencer ao CNPJ emitente da nota</span>
                </div>
                <div className="fin-pp__filters">
                    {/* Obrigatório quando há agência ou conta — mesma regra do servidor.
                        Marcar aqui evita que o fornecedor preencha a seção inteira e só
                        descubra no envio. */}
                    <CampoNA naKey="banco.nome_banco" label="Banco" grow required={!!(dados.banco.agencia || dados.banco.conta)} obrigatorio>
                        <FinInput value={dados.banco.nome_banco} onChange={e => setBanco('nome_banco', e.target.value)} />
                    </CampoNA>
                    <CampoNA naKey="banco.agencia" label="Agência" obrigatorio>
                        <FinInput value={dados.banco.agencia} onChange={e => setBanco('agencia', e.target.value)} />
                    </CampoNA>
                    <CampoNA naKey="banco.conta" label="Conta" obrigatorio>
                        <FinInput value={dados.banco.conta} onChange={e => setBanco('conta', e.target.value)} />
                    </CampoNA>
                    <CampoNA naKey="banco.tipo_conta" label="Tipo de conta" obrigatorio>
                        <ReactSelect
                            options={opcoesDe(TIPOS_CONTA)}
                            placeholder={placeholderDe(TIPOS_CONTA)}
                            isClearable
                            value={dados.banco.tipo_conta ? { value: dados.banco.tipo_conta } : null}
                            onChange={e => setBanco('tipo_conta', e ? e.value : '')}
                        />
                    </CampoNA>
                    {/* Tipo e chave andam em par: um qualifica o outro. Tipo solto o
                        servidor descarta, e chave sem tipo zera o rótulo anterior — nos
                        dois casos o fornecedor sairia achando que declarou o PIX. */}
                    <CampoNA naKey="banco.pix_tipo" label="Tipo de chave PIX" obrigatorio={pixObrigatorio('pix_tipo')}>
                        <ReactSelect
                            options={opcoesDe(TIPOS_PIX)}
                            placeholder={placeholderDe(TIPOS_PIX)}
                            isClearable
                            value={dados.banco.pix_tipo ? { value: dados.banco.pix_tipo } : null}
                            onChange={e => setBanco('pix_tipo', e ? e.value : '')}
                        />
                    </CampoNA>
                    <CampoNA naKey="banco.pix_chave" label="Chave PIX" grow obrigatorio={pixObrigatorio('pix_chave')}>
                        <FinInput value={dados.banco.pix_chave} onChange={e => setBanco('pix_chave', e.target.value)} />
                    </CampoNA>
                    <CampoNA naKey="banco.titular" label="Favorecido" grow obrigatorio>
                        <FinInput value={dados.banco.titular} onChange={e => setBanco('titular', e.target.value)} />
                    </CampoNA>
                    <CampoNA naKey="banco.cnpj_favorecido" label="CNPJ do favorecido" obrigatorio>
                        <FinInput value={dados.banco.cnpj_favorecido} onChange={e => setBanco('cnpj_favorecido', e.target.value)} />
                        {dados.banco.cnpj_favorecido && !cnpjValido(dados.banco.cnpj_favorecido) && (
                            <span style={{ fontSize: 12, color: 'var(--fin-dg)', display: 'block', marginTop: 4 }}>
                                CNPJ inválido.
                            </span>
                        )}
                        {/* AVISO, não trava. O anexo diz que divergência é "devolvida
                            para regularização" — ou seja, quem barra é o revisor, não
                            o formulário. Há caso legítimo (conta de matriz para filial
                            emitente), e bloquear aqui impediria de declará-lo. */}
                        {cnpjValido(dados.banco.cnpj_favorecido) && cnpjValido(dados.cnpj)
                            && dados.banco.cnpj_favorecido.replace(/\D/g, '') !== dados.cnpj.replace(/\D/g, '') && (
                            <span style={{ fontSize: 12, color: 'var(--fin-wn)', display: 'block', marginTop: 4 }}>
                                Diferente do CNPJ da empresa — a conta precisa pertencer ao emitente da nota.
                                Se for proposital, explique no campo de favorecido.
                            </span>
                        )}
                    </CampoNA>
                </div>
                <p style={{ fontSize: 12, color: 'var(--fin-mt)', margin: '2px 0 0' }}>
                    O favorecido deve coincidir com o CNPJ emitente do documento fiscal. Divergências são devolvidas
                    para regularização.
                </p>
            </div>

            {/* ── Seção 4 ─────────────────────────────────────────────────── */}
            <div className="fin-pp__card" style={{ marginBottom: 16, padding: '18px 20px' }}>
                <div className="fin-pp__cphead" style={{ marginBottom: 14 }}>
                    <span className="fin-pp__cphead-title">Objeto do fornecimento</span>
                    <span className="fin-pp__cphead-sub">o que você fornece e onde</span>
                </div>
                <div className="fin-pp__filters">
                    <CampoNA naKey="fiscal.tipo_fornecedor" label="Tipo de fornecedor" obrigatorio>
                        <ReactSelect
                            options={opcoesDe(TIPOS_FORNECEDOR)}
                            placeholder={placeholderDe(TIPOS_FORNECEDOR)}
                            isClearable
                            value={dados.fiscal.tipo_fornecedor ? { value: dados.fiscal.tipo_fornecedor } : null}
                            onChange={e => setFiscal('tipo_fornecedor', e ? e.value : '')}
                        />
                    </CampoNA>
                </div>

                <div style={{ marginTop: 10 }} data-campo="fiscal.documentos_fiscais">
                    {/* Sem N/A e com asterisco: pelo menos um documento é obrigatório —
                        todo fornecedor emite alguma coisa, e a lista cobre até o
                        Recibo/RPA de quem não emite nota. */}
                    <span className="fin-pp__label">
                        Documentos fiscais que você emite
                        <span style={{ color: 'var(--fin-dg)' }}> *</span>
                    </span>
                    <FinChecks>
                        {DOCUMENTOS_FISCAIS.map(doc => (
                            <FinCheck
                                key={doc.valor}
                                label={doc.rotulo}
                                checked={(dados.fiscal.documentos_fiscais || []).includes(doc.valor)}
                                onChange={() => setFiscal('documentos_fiscais', alternarNaLista(dados.fiscal.documentos_fiscais, doc.valor))}
                            />
                        ))}
                    </FinChecks>

                    {/* "Outro" sem dizer qual não responde nada. Mesmo par de
                        `regime_especifico` / `regime_especifico_outro`: a opção abre o
                        campo, e o campo é exigido enquanto ela estiver marcada. */}
                    {(dados.fiscal.documentos_fiscais || []).includes('outro') && (
                        <div className="fin-pp__filters" style={{ marginTop: 10 }}>
                            <CampoNA
                                naKey="fiscal.documentos_fiscais_outro"
                                label="Qual outro documento"
                                full
                                obrigatorio
                            >
                                <FinInput
                                    placeholder="Nome do documento fiscal que você emite e não está na lista"
                                    value={dados.fiscal.documentos_fiscais_outro}
                                    onChange={e => setFiscal('documentos_fiscais_outro', e.target.value)}
                                />
                            </CampoNA>
                        </div>
                    )}
                </div>

                <div className="fin-pp__filters" style={{ marginTop: 10 }}>
                    <CampoNA naKey="fiscal.itens_fornecidos" label="Principais itens fornecidos" full obrigatorio>
                        <FinTextarea
                            rows={2}
                            placeholder="Em palavras — é por aqui que conferimos se os códigos abaixo fazem sentido"
                            value={dados.fiscal.itens_fornecidos}
                            onChange={e => setFiscal('itens_fornecidos', e.target.value)}
                        />
                    </CampoNA>
                    {/* NCM e NBS são nomenclaturas de órgãos diferentes — mercadoria
                        e serviço — e um fornecedor misto declara as duas.

                        O NCM chegou a ser removido daqui sob o argumento de que a
                        lista já existia em `fornecedor_cnae`. Estava errado: aquela
                        tabela guarda CNAE e NBS. O único NCM do sistema é
                        `financeironcm`, domínio de PRODUTO — não há fonte nossa para
                        o NCM de um fornecedor, e sem este campo o dado não era
                        coletado em lugar nenhum. Texto livre porque é declaração dele,
                        não chave que resolvemos contra domínio.

                        Os dois mantêm o N/A individual, mas UM DOS DOIS é obrigatório
                        (ver `pendencias`): quem só vende mercadoria não tem
                        NBS e quem só presta serviço não tem NCM, então exigir os dois
                        obrigaria metade dos fornecedores a inventar o outro. Exigir
                        nenhum deixaria passar cadastro que não diz o que fornece. */}
                    <div className="fin-pp__field fin-pp__field--full" style={{ paddingBottom: 0 }}>
                        <small style={{ color: 'var(--fin-mt)' }}>
                            Informe ao menos um dos dois: NCM se fornece mercadoria, NBS se presta serviço.
                        </small>
                    </div>
                    <CampoNA naKey="fiscal.ncm" label="NCM principais (mercadorias)" full>
                        <FinTextarea
                            rows={2}
                            placeholder="Um código por linha, ou separados por vírgula"
                            value={dados.fiscal.ncm}
                            onChange={e => setFiscal('ncm', e.target.value)}
                        />
                    </CampoNA>
                    <CampoNA naKey="fiscal.nbs" label="NBS principal (serviços)" grow>
                        <FinInput
                            placeholder="Código NBS"
                            value={dados.fiscal.nbs}
                            onChange={e => setFiscal('nbs', e.target.value)}
                        />
                    </CampoNA>
                    <CampoNA naKey="fiscal.locais_execucao" label="Municípios e UFs onde executa" full obrigatorio>
                        <FinTextarea
                            rows={2}
                            placeholder="Ex.: São Paulo/SP, Campinas/SP, atendimento remoto em todo o Brasil"
                            value={dados.fiscal.locais_execucao}
                            onChange={e => setFiscal('locais_execucao', e.target.value)}
                        />
                    </CampoNA>
                </div>
            </div>

            {/* ── Seção 5 ─────────────────────────────────────────────────── */}
            <div className="fin-pp__card" style={{ marginBottom: 16, padding: '18px 20px' }}>
                <div className="fin-pp__cphead" style={{ marginBottom: 14 }}>
                    <span className="fin-pp__cphead-title">Enquadramento tributário</span>
                    <span className="fin-pp__cphead-sub">é o que define as duas seções seguintes</span>
                </div>
                <div className="fin-pp__filters">
                    <CampoNA naKey="fiscal.regime_irpj" label="Regime de tributação" grow obrigatorio>
                        <ReactSelect
                            options={opcoesDe(REGIMES_IRPJ)}
                            placeholder={placeholderDe(REGIMES_IRPJ)}
                            isClearable
                            value={dados.fiscal.regime_irpj ? { value: dados.fiscal.regime_irpj } : null}
                            onChange={e => setRegimeIrpj(e ? e.value : '')}
                        />
                    </CampoNA>
                    {/* O período da opção pelo regime, em semestre/ano, como a área pediu: diz a
                        partir de quando a regra vale, e nota emitida antes dele segue a anterior.
                        Obrigatório para todo regime, ao lado dele. O semestre substitui as duas
                        datas: grava o primeiro dia em `vigencia_opcao`, e o fim é sempre o último
                        dia do mesmo semestre. Base de 260px, e não os 200px do campo comum: com o
                        rótulo longo ele ficava estreito demais ao lado do regime, que segue maior. */}
                    <CampoNA naKey="fiscal.vigencia_opcao" label="Período de vigência da opção (semestre/ano)" obrigatorio style={{ flex: '1 1 260px' }}>
                        <ReactSelect
                            options={opcoesDe(SEMESTRES_VIGENCIA)}
                            placeholder={placeholderDe(SEMESTRES_VIGENCIA)}
                            isClearable
                            value={dados.fiscal.vigencia_opcao ? { value: dados.fiscal.vigencia_opcao } : null}
                            onChange={e => setFiscal('vigencia_opcao', e ? e.value : '')}
                        />
                    </CampoNA>
                    {/* O anexo condiciona: imune/isento exige o fundamento. Sem ele a
                        opção não distingue imunidade de templo de isenção de
                        filantrópica, que têm consequências fiscais diferentes.
                        "Outro" tem a mesma lacuna e a mesma coluna resolve: o rótulo
                        muda com o valor escolhido, sem abrir um segundo campo. */}
                    {(dados.fiscal.regime_irpj === 'imune_isento' || dados.fiscal.regime_irpj === 'outro') && (
                        <CampoNA
                            naKey="fiscal.regime_irpj_fundamento"
                            label={dados.fiscal.regime_irpj === 'outro' ? 'Qual regime' : 'Fundamento da imunidade ou isenção'}
                            obrigatorio
                            grow
                        >
                            <FinInput
                                value={dados.fiscal.regime_irpj_fundamento}
                                onChange={e => setFiscal('regime_irpj_fundamento', e.target.value)}
                            />
                        </CampoNA>
                    )}
                    {/* Só para o optante do Simples, no texto da área: "marque um", entre as
                        duas formas — radio, escolha única. Os demais não respondem: o valor sai do
                        regime de IRPJ (ver `setRegimeIrpj`). */}
                    {dados.fiscal.regime_irpj === 'simples' && (
                        <CampoNA naKey="fiscal.regime_ibs_cbs" label="Forma de recolhimento do IBS/CBS" full obrigatorio>
                            <FinChecks inline>
                                {REGIMES_IBS_CBS.filter(r => FORMAS_SIMPLES.includes(r.valor)).map(r => (
                                    <FinCheck
                                        key={r.valor}
                                        id={'regime_ibs_cbs_' + r.valor}
                                        type="radio"
                                        name="regime_ibs_cbs"
                                        label={r.rotulo}
                                        checked={dados.fiscal.regime_ibs_cbs === r.valor}
                                        onChange={() => setFiscal('regime_ibs_cbs', r.valor)}
                                    />
                                ))}
                            </FinChecks>
                        </CampoNA>
                    )}
                </div>
                {/* As duas datas da opção numa linha só, que não quebra: cada campo encolhe
                    (`minWidth: 0`) em vez de descer para a linha de baixo. */}
                <div className="fin-pp__filters" style={{ flexWrap: 'nowrap' }}>
                    {/* O dia em que ele optou no Portal — não é o início da vigência, que é
                        o semestre em que a opção vale. Sem (*) no pedido da área. */}
                    <CampoNA naKey="fiscal.data_opcao_simples" label="Data da opção no Portal do Simples Nacional" style={{ minWidth: 0 }}>
                        <FinInput
                            type="date"
                            value={dados.fiscal.data_opcao_simples || ''}
                            onChange={e => setFiscal('data_opcao_simples', e.target.value)}
                        />
                    </CampoNA>
                    {/* A data-limite da próxima opção é o que permite cobrar a renovação
                        antes de vencer. */}
                    <CampoNA naKey="fiscal.opcao_proxima_limite" label="Data-limite da próxima opção" style={{ minWidth: 0 }}>
                        <FinInput
                            type="date"
                            value={dados.fiscal.opcao_proxima_limite || ''}
                            onChange={e => setFiscal('opcao_proxima_limite', e.target.value)}
                        />
                    </CampoNA>
                </div>
                <div className="fin-pp__filters" style={{ marginTop: 10 }}>
                    {/* Lista fechada: cada regime tem regra de apuração própria, e
                        "hotelaria" escrito de nove jeitos não é filtrável. */}
                    <CampoNA naKey="fiscal.regime_especifico" label="Enquadrado em regime específico de IBS/CBS?" grow obrigatorio>
                        <ReactSelect
                            options={opcoesDe(REGIMES_ESPECIFICOS)}
                            placeholder={placeholderDe(REGIMES_ESPECIFICOS)}
                            isClearable
                            value={dados.fiscal.regime_especifico ? { value: dados.fiscal.regime_especifico } : null}
                            onChange={e => setFiscal('regime_especifico', e ? e.value : '')}
                        />
                    </CampoNA>
                    {dados.fiscal.regime_especifico === 'outro' && (
                        <CampoNA naKey="fiscal.regime_especifico_outro" label="Qual regime" obrigatorio grow>
                            <FinInput
                                placeholder="Ex.: combustíveis, serviços financeiros, planos de saúde, bens imóveis"
                                value={dados.fiscal.regime_especifico_outro}
                                onChange={e => setFiscal('regime_especifico_outro', e.target.value)}
                            />
                        </CampoNA>
                    )}
                </div>
                {/* Pergunta própria, não mais presa ao regime específico: redução, alíquota
                    zero, isenção e imunidade valem para quem não tem regime específico. Marcar
                    abre o "qual e fundamento" numa linha só para ele; desmarcado é "não possui",
                    e o texto que tenha ficado na tela não vai no envio. O `maxLength` é a largura
                    da coluna, que trunca em silêncio com o `strict` desligado. */}
                <div className="fin-pp__filters" style={{ marginTop: 10 }}>
                    <FinField full>
                        <FinCheck
                            id="possui_reducao"
                            label="Possui redução de alíquota, alíquota zero, isenção ou imunidade"
                            checked={possuiReducao}
                            onChange={e => setPossuiReducao(e.target.checked)}
                        />
                    </FinField>
                    {possuiReducao && (
                        <CampoNA naKey="fiscal.reducao_percentual" label="Qual o benefício e o fundamento legal" full obrigatorio>
                            <FinInput
                                placeholder="Ex.: redução de 60%, com a lei e o artigo"
                                maxLength={255}
                                value={dados.fiscal.reducao_percentual}
                                onChange={e => setFiscal('reducao_percentual', e.target.value)}
                            />
                        </CampoNA>
                    )}
                </div>
                <div className="fin-pp__filters" style={{ marginTop: 10 }}>
                    {/* A resposta já tinha coluna: é o "Indicador de Inscrição" do cadastro
                        interno (`participante.indicadorinscricao`), e a aprovação grava lá.
                        Pergunta de Sim/Não não tem "não se aplica". */}
                    <CampoNA naKey="indicadorinscricao" label="Contribuinte de ICMS?" semNA>
                        <ReactSelect
                            options={opcoesDe(CONTRIBUINTE_ICMS)}
                            placeholder={placeholderDe(CONTRIBUINTE_ICMS)}
                            isClearable
                            value={dados.indicadorinscricao ? { value: dados.indicadorinscricao } : null}
                            onChange={e => set('indicadorinscricao', e ? e.value : '')}
                        />
                    </CampoNA>
                    {/* O par do ICMS. Este não tinha coluna: grava em `contribuinte_iss`. */}
                    <CampoNA naKey="fiscal.contribuinte_iss" label="Contribuinte de ISS?" semNA>
                        <ReactSelect
                            options={opcoesDe(SIM_NAO)}
                            placeholder={placeholderDe(SIM_NAO)}
                            isClearable
                            value={dados.fiscal.contribuinte_iss ? { value: dados.fiscal.contribuinte_iss } : null}
                            onChange={e => setFiscal('contribuinte_iss', e ? e.value : '')}
                        />
                    </CampoNA>
                </div>
            </div>

            {/* ── Seção 6 ─────────────────────────────────────────────────── */}
            <div className="fin-pp__card" style={{ marginBottom: 16, padding: '18px 20px' }}>
                <div className="fin-pp__cphead" style={{ marginBottom: 14 }}>
                    <span className="fin-pp__cphead-title">Emissão e crédito de CBS/IBS</span>
                    <span className="fin-pp__cphead-sub">preencha o que se aplica ao seu regime</span>
                </div>
                <div className="fin-pp__filters">
                    <CampoNA naKey="fiscal.destaque_documentos" label="Destaque nos documentos" grow obrigatorio>
                        <ReactSelect
                            options={opcoesDe(DESTAQUE_DOCUMENTOS)}
                            placeholder={placeholderDe(DESTAQUE_DOCUMENTOS)}
                            isClearable
                            value={dados.fiscal.destaque_documentos ? { value: dados.fiscal.destaque_documentos } : null}
                            onChange={e => setFiscal('destaque_documentos', e ? e.value : '')}
                        />
                    </CampoNA>
                    <CampoNA naKey="fiscal.cst" label="CST" obrigatorio>
                        <FinInput
                            value={dados.fiscal.cst}
                            onChange={e => setFiscal('cst', e.target.value)}
                        />
                    </CampoNA>
                    <CampoNA naKey="fiscal.cclasstrib" label="cClassTrib" obrigatorio>
                        <FinInput
                            value={dados.fiscal.cclasstrib}
                            onChange={e => setFiscal('cclasstrib', e.target.value)}
                        />
                    </CampoNA>
                </div>
                {/* Não/Sim antes da hipótese: campo em branco era ambíguo entre "não
                    aplico" e "não respondi", e na apuração as duas coisas são
                    diferentes. Mesmo motivo que fez a opção vazia entrar explícita nos
                    selects de conta e de PIX. */}
                <div className="fin-pp__filters" style={{ marginTop: 10 }}>
                    <CampoNA naKey="fiscal.credito_presumido_aplica" label="Aplica crédito presumido?" obrigatorio>
                        <ReactSelect
                            options={opcoesDe(SIM_NAO)}
                            placeholder={placeholderDe(SIM_NAO)}
                            isClearable
                            value={dados.fiscal.credito_presumido_aplica ? { value: dados.fiscal.credito_presumido_aplica } : null}
                            onChange={e => setFiscal('credito_presumido_aplica', e ? e.value : '')}
                        />
                    </CampoNA>
                </div>
                <div className="fin-pp__filters" style={{ marginTop: 10 }}>
                    <CampoNA naKey="fiscal.diferimento_aplica" label="Opera com diferimento ou suspensão?" obrigatorio>
                        <ReactSelect
                            options={opcoesDe(SIM_NAO)}
                            placeholder={placeholderDe(SIM_NAO)}
                            isClearable
                            value={dados.fiscal.diferimento_aplica ? { value: dados.fiscal.diferimento_aplica } : null}
                            onChange={e => setFiscal('diferimento_aplica', e ? e.value : '')}
                        />
                    </CampoNA>
                    {dados.fiscal.diferimento_aplica === 'sim' && (
                        <CampoNA label="Em que hipótese" naKey="fiscal.diferimento_suspensao" obrigatorio grow>
                            <FinTextarea
                                rows={2}
                                value={dados.fiscal.diferimento_suspensao}
                                onChange={e => setFiscal('diferimento_suspensao', e.target.value)}
                            />
                        </CampoNA>
                    )}
                </div>
            </div>

            {/* ── Seção 7 ─────────────────────────────────────────────────── */}
            <div className="fin-pp__card" style={{ marginBottom: 16, padding: '18px 20px' }}>
                <div className="fin-pp__cphead" style={{ marginBottom: 6 }}>
                    <span className="fin-pp__cphead-title">Retenções tributárias</span>
                    <span className="fin-pp__cphead-sub">marque o que incide sobre a sua nota</span>
                </div>
                <p style={{ fontSize: 12, color: 'var(--fin-mt)', margin: '0 0 12px' }}>
                    Deixe desmarcado o que não incide. A alíquota é opcional — informe quando ela for
                    diferente da padrão.
                </p>
                {/* "Não se aplica" da seção inteira.
                    Sem isto, quem lê a instrução acima e não marca nada manda um formulário
                    idêntico ao de quem nem rolou até aqui — e a declaração "não retenho
                    nada" era descartada na aprovação, sem ninguém perceber. Marcar é
                    afirmar a ausência, e é isso que o financeiro precisa distinguir. */}
                <div style={{ margin: '0 0 12px' }}>
                    <FinCheck
                        id="sem_retencoes"
                        label="Nenhuma retenção incide sobre as minhas notas"
                        checked={Boolean(dados.fiscal.sem_retencoes)}
                        onChange={e => marcarSemRetencoes(e.target.checked)}
                    />
                </div>
                <div className="fin-pp__tblwrap">
                    <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                        <thead>
                            <tr>
                                <th>Tributo</th>
                                <th style={{ width: 90 }}>Incide</th>
                                <th style={{ width: 140 }}>Alíquota</th>
                                <th>Observação</th>
                            </tr>
                        </thead>
                        <tbody>
                            {TRIBUTOS_RETENCAO.map(t => (
                                <tr key={t.valor}>
                                    <td className="fin-pp__cell-strong">{t.rotulo}</td>
                                    <td>
                                        <input
                                            type="checkbox"
                                            className="fin-pp__check"
                                            checked={Boolean(dados.fiscal.retencoes?.[t.valor]?.aplica)}
                                            disabled={Boolean(dados.fiscal.sem_retencoes)}
                                            onChange={e => setRetencao(t.valor, 'aplica', e.target.checked)}
                                        />
                                    </td>
                                    <td>
                                        <FinInput
                                            placeholder="Ex.: 1,5%"
                                            disabled={!dados.fiscal.retencoes?.[t.valor]?.aplica}
                                            value={dados.fiscal.retencoes?.[t.valor]?.aliquota || ''}
                                            onChange={e => setRetencao(t.valor, 'aliquota', e.target.value)}
                                        />
                                    </td>
                                    {/* A observação do anexo, não a esfera. A esfera é
                                        classificação da Access e fica na tela do
                                        operador; aqui interessa o que ELE tem a dizer
                                        sobre a retenção — tipicamente até quando ela
                                        vale, que é o ponto da nota da seção 7. */}
                                    <td>
                                        <FinInput
                                            placeholder="Ex.: retido pela tomadora até 31/12/2026"
                                            maxLength={255}
                                            disabled={!dados.fiscal.retencoes?.[t.valor]?.aplica}
                                            value={dados.fiscal.retencoes?.[t.valor]?.observacao || ''}
                                            onChange={e => setRetencao(t.valor, 'observacao', e.target.value)}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="fin-pp__filters" style={{ marginTop: 10 }}>
                    {/* A linha "Outras" da tabela da seção 7 — distinta da observação
                        abaixo: aqui é mais um tributo retido, ali é o comentário sobre
                        o período em que PIS/COFINS/CSLL e ISS ainda coexistem com o
                        novo modelo. */}
                    <CampoNA naKey="fiscal.retencoes_outras" label="Outras retenções" full>
                        <FinTextarea
                            rows={2}
                            placeholder="Tributo e alíquota, se houver algum fora da tabela acima"
                            value={dados.fiscal.retencoes_outras}
                            onChange={e => setFiscal('retencoes_outras', e.target.value)}
                        />
                    </CampoNA>
                    {/* Os dois já existiam no cadastro fiscal antes desta entrega:
                        `observacao_fiscal` é o campo livre do anexo sobre a transição,
                        e `data_transicao_iva` é a vigência dela. */}
                    <CampoNA naKey="fiscal.observacao_fiscal" label="Observações sobre retenções na transição" full>
                        <FinTextarea
                            rows={2}
                            placeholder="Ex.: ISS retido até 31/12/2026; PIS e COFINS cessam com a extinção"
                            value={dados.fiscal.observacao_fiscal}
                            onChange={e => setFiscal('observacao_fiscal', e.target.value)}
                        />
                    </CampoNA>
                    <CampoNA naKey="fiscal.data_transicao_iva" label="Data da transição do IVA">
                        <FinInput
                            type="date"
                            value={dados.fiscal.data_transicao_iva || ''}
                            onChange={e => setFiscal('data_transicao_iva', e.target.value)}
                        />
                    </CampoNA>
                </div>
            </div>

            {/* ── Seção 9 ─────────────────────────────────────────────────── */}
            <div className="fin-pp__card" style={{ marginBottom: 16, padding: '18px 20px' }}>
                <div className="fin-pp__cphead" style={{ marginBottom: 6 }}>
                    <span className="fin-pp__cphead-title">Documentos</span>
                    <span className="fin-pp__cphead-sub">PDF, PNG ou JPG, até 10 MB cada</span>
                </div>
                <p style={{ fontSize: 12, color: 'var(--fin-mt)', margin: '0 0 12px' }}>
                    Os arquivos ficam só neste navegador até você enviar o cadastro, e sobem junto
                    com ele. Dá para remover antes de enviar.
                </p>

                <div className="fin-pp__filters">
                    {TIPOS_DOCUMENTO.map(tipo => {
                        const obrigatorio = documentosExigidos().includes(tipo.valor);
                        const multiplo = tipo.valor === 'outro';
                        // Sem N/A: o "Outro" é opcional por natureza, e o tipo que já tem arquivo
                        // já respondeu que se aplica.
                        const semNA = multiplo || documentos.some(doc => doc.tipo === tipo.valor);
                        // O mesmo cálculo do CampoNA: marcado como N/A, o botão não anexa.
                        const naoAplica = !obrigatorio && !semNA
                            && (dados.nao_aplica || []).includes('documentos.' + tipo.valor);

                        return (
                            <CampoNA
                                naKey={'documentos.' + tipo.valor}
                                key={tipo.valor}
                                label={tipo.rotulo}
                                grow
                                obrigatorio={obrigatorio}
                                semNA={semNA}
                            >
                                <label
                                    className="fin-pp__btn fin-pp__btn--sm"
                                    style={{ cursor: naoAplica ? 'not-allowed' : 'pointer', width: '100%', justifyContent: 'center' }}
                                >
                                    <FaPaperclip size={12} />
                                    {multiplo ? ' Anexar arquivos' : ' Anexar'}
                                    <input
                                        type="file"
                                        accept=".pdf,.png,.jpg,.jpeg"
                                        multiple={multiplo}
                                        disabled={naoAplica}
                                        style={{ display: 'none' }}
                                        onChange={e => {
                                            anexar(tipo.valor, Array.from(e.target.files || []));
                                            // Zera para que escolher o MESMO arquivo de novo
                                            // volte a disparar o onChange.
                                            e.target.value = '';
                                        }}
                                    />
                                </label>
                            </CampoNA>
                        );
                    })}
                </div>

                {erroAnexo && (
                    <div className="fin-pp__banner is-warn" style={{ marginTop: 12 }}>{erroAnexo}</div>
                )}

                {documentos.length > 0 && (
                    <div className="fin-pp__tblwrap" style={{ marginTop: 14 }}>
                        <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                            <thead>
                                <tr>
                                    <th>Tipo</th>
                                    <th>Arquivo</th>
                                    <th style={{ width: 90 }}>Tamanho</th>
                                    <th style={{ width: 60 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {documentos.map(doc => (
                                    <tr key={doc.chave}>
                                        <td className="fin-pp__cell-muted">{rotuloDe(TIPOS_DOCUMENTO, doc.tipo)}</td>
                                        <td className="fin-pp__cell-strong">{doc.arquivo.name}</td>
                                        <td className="fin-pp__cell-muted">{formatarTamanho(doc.arquivo.size)}</td>
                                        <td>
                                            <button
                                                type="button"
                                                className="fin-pp__btn fin-pp__btn--sm"
                                                title="Remover"
                                                onClick={() => removerAnexo(doc.chave)}
                                            >
                                                <FaTrash size={11} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ── Seção 10 ────────────────────────────────────────────────── */}
            <div className="fin-pp__card" style={{ marginBottom: 16, padding: '18px 20px' }}>
                <div className="fin-pp__cphead" style={{ marginBottom: 6 }}>
                    <span className="fin-pp__cphead-title">Declarações</span>
                    <span className="fin-pp__cphead-sub">todas são obrigatórias</span>
                </div>
                <p style={{ fontSize: 12, color: 'var(--fin-mt)', margin: '0 0 12px' }}>
                    Ao enviar, registramos a data, o endereço de origem e a versão deste texto.
                </p>

                {/* O texto vem do SERVIDOR (`contexto.declaracoes`), não daqui. É a
                    mesma fonte que gera o hash do aceite: se a tela guardasse o texto,
                    trocá-lo em um deploy faria os aceites antigos apontarem para algo
                    que ninguém leu. */}
                <div className="fin-pp__filters" style={{ marginBottom: 14 }}>
                    <CampoNA naKey="assinante_nome" label="Nome do representante legal" grow obrigatorio>
                        <FinInput value={dados.assinante_nome} onChange={e => set('assinante_nome', e.target.value)} />
                    </CampoNA>
                    <CampoNA naKey="assinante_cpf" label="CPF" required obrigatorio>
                        <FinInput value={dados.assinante_cpf} onChange={e => set('assinante_cpf', e.target.value)} />
                        {dados.assinante_cpf && !cpfValido(dados.assinante_cpf) && (
                            <span style={{ fontSize: 12, color: 'var(--fin-dg)', display: 'block', marginTop: 4 }}>
                                CPF inválido.
                            </span>
                        )}
                    </CampoNA>
                    <CampoNA label="Cargo" naKey="assinante_cargo" grow>
                        <FinInput value={dados.assinante_cargo} onChange={e => set('assinante_cargo', e.target.value)} />
                    </CampoNA>
                    <CampoNA naKey="assinatura_local" label="Local">
                        <FinInput value={dados.assinatura_local} onChange={e => set('assinatura_local', e.target.value)} />
                    </CampoNA>
                    {/* Data em branco vale a do envio — o servidor preenche. Deixar o
                        campo aberto atende ao "local e data" do anexo sem obrigar
                        ninguém a digitar a data de hoje. */}
                    <CampoNA naKey="assinatura_data" label="Data" obrigatorio>
                        <FinInput
                            type="date"
                            value={dados.assinatura_data || ''}
                            onChange={e => set('assinatura_data', e.target.value)}
                        />
                    </CampoNA>
                </div>

                <div className="fin-pp__checks">
                    {(contexto?.declaracoes || []).map(dec => (
                        <label key={dec.chave} className="fin-pp__check-row" style={{ alignItems: 'flex-start' }}>
                            <input
                                type="checkbox"
                                className="fin-pp__check"
                                style={{ marginTop: 3 }}
                                data-campo={'aceite.' + dec.chave}
                                checked={(dados.aceite || []).includes(dec.chave)}
                                onChange={() => set('aceite', alternarNaLista(dados.aceite, dec.chave))}
                            />
                            <span className="fin-pp__check-label" style={{ lineHeight: 1.5 }}>{dec.texto}</span>
                        </label>
                    ))}
                </div>
            </div>

            {erroEnvio && (
                <div className="fin-pp__banner is-warn" style={{ marginBottom: 12 }}>{erroEnvio}</div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: 40 }}>
                <button
                    type="button"
                    className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--tall"
                    onClick={tentarEnviar}
                    // Só o envio em curso e a amostra travam. O que falta preencher é
                    // conferido no clique — ver `tentarEnviar` e `pendencias()`.
                    disabled={enviando || !!contexto?.amostra}
                >
                    {contexto?.amostra
                        ? 'Envio desativado na amostra'
                        : (enviando ? 'Enviando…' : 'Enviar cadastro')}
                </button>
            </div>
        </Moldura>
        </NaoAplicaContext.Provider>
    );
}
